import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { toast } from "react-toastify";
import Table from "../../../core/common/dataTable/index";
import axios from "axios";
import { tokenService } from "../../../core/utils/tokenService";
import { matchCandidateRecord } from "./probationSearchUtils";

export const getEmpIdKeys = (val: any): string[] => {
  if (val === null || val === undefined) return [];
  const str = String(val).trim();
  if (!str || str === "—") return [];
  const keys = [str];
  const unpadded = str.replace(/^0+/, "");
  if (unpadded && unpadded !== str) {
    keys.push(unpadded);
  }
  if (/^\d+$/.test(unpadded)) {
    const padded8 = unpadded.padStart(8, "0");
    if (padded8 !== str && !keys.includes(padded8)) {
      keys.push(padded8);
    }
  }
  return keys;
};

export const getLatestPvrRecord = (c: any): any => {
  if (!c || typeof c !== "object") return null;

  if (Array.isArray(c)) return c[0] || null;

  if (c.latestPvrRecord) {
    if (Array.isArray(c.latestPvrRecord)) return c.latestPvrRecord[0] || null;
    if (typeof c.latestPvrRecord === "object") return c.latestPvrRecord;
  }
  if (c.latestpvrrecord) {
    if (Array.isArray(c.latestpvrrecord)) return c.latestpvrrecord[0] || null;
    if (typeof c.latestpvrrecord === "object") return c.latestpvrrecord;
  }
  if (c.latest_pvr_record) {
    if (Array.isArray(c.latest_pvr_record)) return c.latest_pvr_record[0] || null;
    if (typeof c.latest_pvr_record === "object") return c.latest_pvr_record;
  }
  if (c.pvrRecord) {
    if (Array.isArray(c.pvrRecord)) return c.pvrRecord[0] || null;
    if (typeof c.pvrRecord === "object") return c.pvrRecord;
  }
  if (c.pvr_record) {
    if (Array.isArray(c.pvr_record)) return c.pvr_record[0] || null;
    if (typeof c.pvr_record === "object") return c.pvr_record;
  }

  const emp = c.employee || c.candidate;
  if (emp && typeof emp === "object") {
    if (emp.latestPvrRecord) {
      if (Array.isArray(emp.latestPvrRecord)) return emp.latestPvrRecord[0] || null;
      if (typeof emp.latestPvrRecord === "object") return emp.latestPvrRecord;
    }
    if (emp.latestpvrrecord) {
      if (Array.isArray(emp.latestpvrrecord)) return emp.latestpvrrecord[0] || null;
      if (typeof emp.latestpvrrecord === "object") return emp.latestpvrrecord;
    }
    if (emp.latest_pvr_record) {
      if (Array.isArray(emp.latest_pvr_record)) return emp.latest_pvr_record[0] || null;
      if (typeof emp.latest_pvr_record === "object") return emp.latest_pvr_record;
    }
    if (emp.pvrRecord) {
      if (Array.isArray(emp.pvrRecord)) return emp.pvrRecord[0] || null;
      if (typeof emp.pvrRecord === "object") return emp.pvrRecord;
    }
    if (emp.pvr_record) {
      if (Array.isArray(emp.pvr_record)) return emp.pvr_record[0] || null;
      if (typeof emp.pvr_record === "object") return emp.pvr_record;
    }
    if (emp.zpvr1 !== undefined || emp.zpvr !== undefined || emp.pvrStatus !== undefined || emp.pvr !== undefined) return emp;
  }

  if (c.zpvr1 !== undefined || c.zpvr !== undefined || c.pvrStatus !== undefined || c.pvr !== undefined) return c;

  return c;
};

export const PVR_STATUS_OPTIONS = ["NO ADVERSE", "ISSUE IN PVR", "In Process"] as const;

export const normalizePvrStatus = (val: any): "NO ADVERSE" | "ISSUE IN PVR" | "In Process" => {
  if (!val) return "In Process";
  const str = String(val).trim().toUpperCase();
  if (str === "NO ADVERSE" || str === "NO_ADVERSE" || str === "CLEAR" || str === "PVR CLEAR" || str === "PVR_CLEAR") {
    return "NO ADVERSE";
  }
  if (str === "ISSUE IN PVR" || str === "ISSUE_IN_PVR" || str === "ADVERSE" || str === "ADVERSE REPORT" || str === "ADVERSE_REPORT") {
    return "ISSUE IN PVR";
  }
  return "In Process";
};

export const getNonEmptyPvrRemark = (...values: any[]): string => {
  for (const v of values) {
    if (v !== null && v !== undefined) {
      const s = String(v).trim();
      if (s.length > 0 && s !== "null" && s !== "undefined" && s !== "—" && s !== "-") {
        return s;
      }
    }
  }
  return "";
};

export const mapPvrRecord = (candidateOrRecord: any): { pvrStatus: "NO ADVERSE" | "ISSUE IN PVR" | "In Process"; pvrRemarks: string } => {
  if (!candidateOrRecord || typeof candidateOrRecord !== "object") {
    return { pvrStatus: "In Process", pvrRemarks: "—" };
  }

  const latestPvrRecord = getLatestPvrRecord(candidateOrRecord) || candidateOrRecord;
  const pvrEval = candidateOrRecord?.pvr_evaluation || candidateOrRecord?.pvrEvaluation || candidateOrRecord?.pvrDetails || candidateOrRecord?.pvr_details;

  const rawZpvr1 =
    latestPvrRecord?.zpvr1 ??
    candidateOrRecord?.zpvr1 ??
    latestPvrRecord?.zpvr ??
    candidateOrRecord?.zpvr ??
    latestPvrRecord?.zpvr2 ??
    candidateOrRecord?.zpvr2 ??
    latestPvrRecord?.pvrStatus ??
    candidateOrRecord?.pvrStatus ??
    latestPvrRecord?.pvrStatusText ??
    candidateOrRecord?.pvrStatusText ??
    latestPvrRecord?.pvr ??
    candidateOrRecord?.pvr ??
    pvrEval?.zpvr1 ??
    pvrEval?.zpvr ??
    pvrEval?.pvrStatus ??
    "";

  const zpvr1Str = String(rawZpvr1 ?? "").trim().toUpperCase();

  let pvrStatus: "NO ADVERSE" | "ISSUE IN PVR" | "In Process" = "In Process";

  if (
    zpvr1Str === "NO ADVERSE" ||
    zpvr1Str === "NO_ADVERSE" ||
    zpvr1Str === "CLEAR" ||
    zpvr1Str === "PVR CLEAR" ||
    zpvr1Str === "PVR_CLEAR"
  ) {
    pvrStatus = "NO ADVERSE";
  } else if (
    zpvr1Str === "ISSUE IN PVR" ||
    zpvr1Str === "ISSUE_IN_PVR" ||
    zpvr1Str === "ADVERSE" ||
    zpvr1Str === "ADVERSE REPORT" ||
    zpvr1Str === "ADVERSE_REPORT"
  ) {
    pvrStatus = "ISSUE IN PVR";
  } else {
    pvrStatus = "In Process";
  }

  const remarksText = getNonEmptyPvrRemark(
    latestPvrRecord?.remarks,
    latestPvrRecord?.zrem,
    latestPvrRecord?.otherReasons,
    latestPvrRecord?.other_reasons,
    latestPvrRecord?.other_reason,
    latestPvrRecord?.pvrRemarks,
    latestPvrRecord?.pvr_remarks,
    pvrEval?.remarks,
    pvrEval?.zrem,
    pvrEval?.otherReasons,
    pvrEval?.other_reasons,
    pvrEval?.other_reason,
    pvrEval?.pvrRemarks,
    candidateOrRecord?.pvrRemarks,
    candidateOrRecord?.pvr_remarks,
    candidateOrRecord?.zrem,
    candidateOrRecord?.otherReasons,
    candidateOrRecord?.other_reasons,
    candidateOrRecord?.other_reason
  );

  const pvrRemarks = remarksText.length > 0 ? remarksText : "—";

  return { pvrStatus, pvrRemarks };
};

export const getPvrBadgeTheme = (status: string) => {
  const norm = normalizePvrStatus(status);
  if (norm === "NO ADVERSE") {
    return { color: "#15803d", background: "#f0fdf4", border: "1px solid #86efac" };
  }
  if (norm === "ISSUE IN PVR") {
    return { color: "#b91c1c", background: "#fef2f2", border: "1px solid #fca5a5" };
  }
  return { color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a" };
};

export const getPvrStatusTheme = getPvrBadgeTheme;


const VIGILANCE_STATUS_OPTIONS = [
  "No Vigilance Case",
  "Vigilance Initiated",
  "Penalty Awarded",
];

const normalizeVigilanceStatus = (value: any) => {
  if (!value || value === "PENDING" || value === "Pending" || value === "NO_DATA" || value === "NO DATA") {
    return "No Vigilance Case";
  }
  const normalized = String(value).trim().toUpperCase().replace(/_/g, " ");
  if (normalized === "NO VIGILANCE CASE" || normalized === "CLEAR" || normalized === "CLEARED" || normalized === "PASSED") {
    return "No Vigilance Case";
  }
  if (normalized === "PENALTY AWARDED" || normalized === "PENALTY") {
    return "Penalty Awarded";
  }
  if (normalized === "VIGILANCE INITIATED" || normalized === "INITIATED" || normalized === "NOT CLEAR" || normalized === "NOT_CLEAR") {
    return "Vigilance Initiated";
  }
  return "No Vigilance Case";
};

const getVigilanceStatusTheme = (status: string) => {
  const s = normalizeVigilanceStatus(status);
  if (s === "No Vigilance Case") {
    return { color: "#15803d", background: "#f0fdf4", border: "#86efac" };
  }
  if (s === "Penalty Awarded") {
    return { color: "#b91c1c", background: "#fef2f2", border: "#fca5a5" };
  }
  return { color: "#a16207", background: "#fefce8", border: "#fde047" };
};

const formatDateDMY = (dateVal: any): string => {
  if (!dateVal || dateVal === "N/A" || dateVal === "null" || dateVal === "undefined" || dateVal === "—" || dateVal === "-") {
    return "—";
  }
  const str = String(dateVal).trim();
  if (!str) return "—";
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split("-");
    return `${d}/${m}/${y}`;
  }
  if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
    const datePart = str.split("T")[0];
    const [y, m, d] = datePart.split("-");
    return `${d}/${m}/${y}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    return str;
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    return str.replace(/-/g, "/");
  }
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    const day = String(parsed.getDate()).padStart(2, "0");
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const year = parsed.getFullYear();
    return `${day}/${month}/${year}`;
  }
  return str || "—";
};

const getDarDetails = (c: any) => {
  if (!c) {
    return {
      statusText: "No D&AR Record",
      badgeClass: "bg-secondary text-white",
      badgeStyle: { backgroundColor: "#64748b", color: "#ffffff", fontWeight: 600, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
      caseNo: "—",
      chargeSheetIssuedDate: "—",
      penaltyDescription: "—",
      dateOfAction: "—",
      penaltyStartDate: "—",
      penaltyEndDate: "—"
    };
  }

  const evalObj = c.dar_evaluation || c.darEvaluation || c.darDetails || c.dar_details || c;
  const darCleared = evalObj.darCleared ?? evalObj.dar_cleared ?? c.darCleared ?? c.dar_cleared;
  const rawStatus = (evalObj.status ?? evalObj.darStatus ?? evalObj.dar_status ?? c.status ?? c.darStatus ?? c.dar_status ?? "").toString().trim();
  const caseNoRaw = (evalObj.caseNo ?? evalObj.case_no ?? c.caseNo ?? c.case_no ?? c.darDetails?.caseNo ?? c.dar_details?.caseNo ?? "").toString().trim();
  const chargeSheetDateRaw = evalObj.chargeSheetIssuedDate ?? evalObj.charge_sheet_issued_date ?? c.chargeSheetIssuedDate ?? c.charge_sheet_issued_date ?? c.darDetails?.chargeSheetIssuedDate ?? c.dar_details?.chargeSheetIssuedDate;
  const penaltyDescRaw = (evalObj.penaltyDescription ?? evalObj.penalty_description ?? c.penaltyDescription ?? c.penalty_description ?? c.darDetails?.penaltyDescription ?? "").toString().trim();
  const dateOfActionRaw = evalObj.dateOfAction ?? evalObj.date_of_action ?? c.dateOfAction ?? c.date_of_action ?? c.darDetails?.dateOfAction;
  const penaltyStartRaw = evalObj.penaltyStartDate ?? evalObj.penalty_start_date ?? c.penaltyStartDate ?? c.penalty_start_date ?? c.darDetails?.penaltyStartDate;
  const penaltyEndRaw = evalObj.penaltyEndDate ?? evalObj.penalty_end_date ?? c.penaltyEndDate ?? c.penalty_end_date ?? c.darDetails?.penaltyEndDate;

  const formatVal = (val: string) => (!val || val === "null" || val === "undefined" || val === "N/A" ? "—" : val);

  const statusLower = rawStatus.toLowerCase();

  if (statusLower === "dar clear" || statusLower === "dar_clear" || statusLower === "clear" || statusLower === "cleared" || (statusLower === "" && darCleared === true)) {
    return {
      statusText: "DAR CLEAR",
      badgeClass: "bg-success text-white",
      badgeStyle: { backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
      caseNo: formatVal(caseNoRaw),
      chargeSheetIssuedDate: formatDateDMY(chargeSheetDateRaw),
      penaltyDescription: formatVal(penaltyDescRaw),
      dateOfAction: formatDateDMY(dateOfActionRaw),
      penaltyStartDate: formatDateDMY(penaltyStartRaw),
      penaltyEndDate: formatDateDMY(penaltyEndRaw)
    };
  }

  if (statusLower === "chargesheet issued" || statusLower === "chargesheet_issued" || statusLower === "chargesheetissued") {
    return {
      statusText: "ChargeSheet Issued",
      badgeClass: "bg-warning text-dark",
      badgeStyle: { backgroundColor: "#fff7ed", color: "#c2410c", border: "1.5px solid #fdba74", fontWeight: 700, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
      caseNo: formatVal(caseNoRaw),
      chargeSheetIssuedDate: formatDateDMY(chargeSheetDateRaw),
      penaltyDescription: formatVal(penaltyDescRaw),
      dateOfAction: formatDateDMY(dateOfActionRaw),
      penaltyStartDate: formatDateDMY(penaltyStartRaw),
      penaltyEndDate: formatDateDMY(penaltyEndRaw)
    };
  }

  if (statusLower === "minor penalty" || statusLower === "minor_penalty" || statusLower === "minorpenalty") {
    return {
      statusText: "Minor Penalty",
      badgeClass: "bg-warning text-dark",
      badgeStyle: { backgroundColor: "#fefce8", color: "#a16207", border: "1.5px solid #fde047", fontWeight: 700, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
      caseNo: formatVal(caseNoRaw),
      chargeSheetIssuedDate: formatDateDMY(chargeSheetDateRaw),
      penaltyDescription: formatVal(penaltyDescRaw),
      dateOfAction: formatDateDMY(dateOfActionRaw),
      penaltyStartDate: formatDateDMY(penaltyStartRaw),
      penaltyEndDate: formatDateDMY(penaltyEndRaw)
    };
  }

  if (statusLower === "major penalty" || statusLower === "major_penalty" || statusLower === "majorpenalty") {
    return {
      statusText: "Major Penalty",
      badgeClass: "bg-danger text-white",
      badgeStyle: { backgroundColor: "#b91c1c", color: "#ffffff", fontWeight: 700, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
      caseNo: formatVal(caseNoRaw),
      chargeSheetIssuedDate: formatDateDMY(chargeSheetDateRaw),
      penaltyDescription: formatVal(penaltyDescRaw),
      dateOfAction: formatDateDMY(dateOfActionRaw),
      penaltyStartDate: formatDateDMY(penaltyStartRaw),
      penaltyEndDate: formatDateDMY(penaltyEndRaw)
    };
  }

  if (!rawStatus || rawStatus === "true" || rawStatus === "false") {
    const isCleared = darCleared === true;
    return {
      statusText: isCleared ? "DAR CLEAR" : "No D&AR Record",
      badgeClass: isCleared ? "bg-success text-white" : "bg-secondary text-white",
      badgeStyle: isCleared
        ? { backgroundColor: "#15803d", color: "#ffffff", fontWeight: 700, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" }
        : { backgroundColor: "#64748b", color: "#ffffff", fontWeight: 600, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
      caseNo: formatVal(caseNoRaw),
      chargeSheetIssuedDate: formatDateDMY(chargeSheetDateRaw),
      penaltyDescription: formatVal(penaltyDescRaw),
      dateOfAction: formatDateDMY(dateOfActionRaw),
      penaltyStartDate: formatDateDMY(penaltyStartRaw),
      penaltyEndDate: formatDateDMY(penaltyEndRaw)
    };
  }

  return {
    statusText: rawStatus,
    badgeClass: "bg-secondary text-white",
    badgeStyle: { backgroundColor: "#475569", color: "#ffffff", fontWeight: 600, padding: "5px 12px", fontSize: "12px", borderRadius: "6px", letterSpacing: "0.2px" },
    caseNo: formatVal(caseNoRaw),
    chargeSheetIssuedDate: formatDateDMY(chargeSheetDateRaw),
    penaltyDescription: formatVal(penaltyDescRaw),
    dateOfAction: formatDateDMY(dateOfActionRaw),
    penaltyStartDate: formatDateDMY(penaltyStartRaw),
    penaltyEndDate: formatDateDMY(penaltyEndRaw)
  };
};

const StepBubble: React.FC<{ index: number; current: number; label: string; onClick?: () => void }> = ({ index, current, label, onClick }) => {
  const isDone = index < current;
  const isActive = index === current;
  return (
    <div className="text-center position-relative px-2" onClick={onClick} style={{ cursor: onClick ? "pointer" : "default" }}>
      <div
        className={`rounded-circle d-inline-flex align-items-center justify-content-center fw-bold transition-all ${isDone ? "bg-success text-white" : isActive ? "bg-danger text-white shadow" : "bg-light text-muted"
          }`}
        style={{
          width: "36px",
          height: "36px",
          border: isActive ? "2px solid #fff" : "none",
          fontSize: "14px",
          zIndex: 2
        }}
      >
        {isDone ? <i className="ti ti-check" style={{ fontSize: "14px" }}></i> : index}
      </div>
      <div className={`small mt-2 ${isActive ? "fw-semibold text-danger" : "text-muted"}`} style={{ fontSize: "12px" }}>
        {label}
      </div>
    </div>
  );
};

const NewProbationCycle: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlCycleId = queryParams.get("cycleId");
  const urlStep = queryParams.get("step");

  const initialStep = (() => {
    const s = Number(urlStep);
    return s >= 1 && s <= 7 ? s : 1;
  })();

  const [step, setStep] = useState<number>(initialStep);
  const [cycleId, setCycleId] = useState<number | string | null>(urlCycleId || null);

  const updateStep = (targetStep: number, targetCycleId?: number | string | null) => {
    const activeCycleId = targetCycleId !== undefined ? targetCycleId : cycleId;
    setStep(targetStep);
    if (activeCycleId) {
      setCycleId(activeCycleId);
    }
    const params = new URLSearchParams(location.search);
    if (activeCycleId) {
      params.set("cycleId", String(activeCycleId));
    }
    params.set("step", String(targetStep));
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // Step 1: Initiate State
  const [title, setTitle] = useState("");
  // Dynamically compute previous financial year (e.g. if current year is 2026, default to 2025-2026)
  const currentYear = new Date().getFullYear();
  const prevFY = `${currentYear - 1}-${currentYear}`;
  const [financialYear, setFinancialYear] = useState(prevFY);
  const [employeeType, setEmployeeType] = useState("");
  const [employmentType, setEmploymentType] = useState("");
  const [employmentTypesList, setEmploymentTypesList] = useState<any[]>([]);
  const [loadingEmploymentTypes, setLoadingEmploymentTypes] = useState(false);
  const [grades, setGrades] = useState<string[]>([]);
  const [gradeDropdownOpen, setGradeDropdownOpen] = useState(false);
  const gradeDropdownRef = useRef<HTMLDivElement>(null);
  const [confirmationDueOn, setConfirmationDueOn] = useState("");
  const [employeeRange, setEmployeeRange] = useState("");
  const [gradesMaster, setGradesMaster] = useState<any[]>([]);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [payScaleSearch, setPayScaleSearch] = useState("");
  const [initiatedCycle, setInitiatedCycle] = useState<any>(null);
  const [isInitiating, setIsInitiating] = useState(false);

  useEffect(() => {
    const fetchEmploymentTypes = async () => {
      setLoadingEmploymentTypes(true);
      try {
        const response = await Api.get(API_URLS.PROBATION.EMPLOYEE_TYPES, true);
        const resData = response?.data?.data ?? response?.data ?? response;
        const list = Array.isArray(resData)
          ? resData
          : Array.isArray(resData?.items)
            ? resData.items
            : Array.isArray(resData?.types)
              ? resData.types
              : Array.isArray(resData?.data)
                ? resData.data
                : [];
        setEmploymentTypesList(list);
      } catch (err) {
        console.error("Failed to fetch employment types", err);
      } finally {
        setLoadingEmploymentTypes(false);
      }
    };

    fetchEmploymentTypes();
  }, []);

  useEffect(() => {
    if (!urlCycleId) return;

    const loadActiveCycle = async () => {
      try {
        setLoadingCandidates(true);
        // Fetch cycle lists to find this cycle's status
        const response = await Api.get(API_URLS.PROBATION.CYCLES, true);
        const resData = response?.data || response;
        const list = Array.isArray(resData?.data) ? resData.data : Array.isArray(resData) ? resData : [];

        const cycle = list.find((c: any) => String(c.id || c.cycleId || c.cycle_id) === String(urlCycleId));
        if (cycle) {
          setCycleId(cycle.id || cycle.cycleId || cycle.cycle_id);
          setInitiatedCycle(cycle);
          if (cycle.financialYear || cycle.financial_year) setFinancialYear(cycle.financialYear || cycle.financial_year);
          if (cycle.employeeType || cycle.employee_type) setEmployeeType(cycle.employeeType || cycle.employee_type);
          if (cycle.employmentType || cycle.employment_type) setEmploymentType(formatEmploymentTypeText(cycle.employmentType || cycle.employment_type));
          if (cycle.grade) {
            try {
              setGrades(typeof cycle.grade === "string" ? JSON.parse(cycle.grade) : cycle.grade);
            } catch (e) { }
          }
          setConfirmationDueOn(
            cycle.confirmationDueOn || cycle.confirmation_due_on || cycle.probationDueDate || cycle.probation_due_date || cycle.probationToDate || ""
          );
          if (cycle.employeeRange || cycle.employee_range) setEmployeeRange(cycle.employeeRange || cycle.employee_range);

          // Determine step based on query param or status
          const urlStepNum = Number(urlStep);
          if (urlStepNum >= 1 && urlStepNum <= 7) {
            setStep(urlStepNum);
          } else {
            const status = (cycle.status || "").toUpperCase();
            let targetStep = 2;
            if (status === "INITIATED") {
              targetStep = 2;
            } else if (status === "PVR_CLEARED" || status === "PVR_CLEAR" || status === "PVR_STATUS_SET" || status === "PVR_CALCULATED" || status === "PVR_DONE") {
              targetStep = 3;
            } else if (status === "ELIGIBILITY_CALCULATED" || status === "CALCULATED" || status === "CANDIDATES_CALCULATED") {
              targetStep = 4;
            } else if (status === "WR_GRADING" || status === "WR_CLEARED" || status === "WR_CLEAR" || status === "WR_SUITABLE" || status === "WR_CALCULATED" || status === "WR_STATUS_SET") {
              targetStep = 5;
            } else if (status === "DAR_CLEARED" || status === "DAR_CLEAR" || status === "DAR_STATUS_SET" || status === "DAR_CALCULATED" || status === "DAR_SUITABLE") {
              targetStep = 6;
            } else if (status === "VIGILANCE_CLEARED" || status === "VIGILANCE_CLEAR" || status === "VIGILANCE_STATUS_SET" || status === "VIGILANCE_CALCULATED" || status === "VIGILANCE_UPDATED" || status === "ELIGIBLE_GENERATED" || status === "SCREENING_COMMITTEE" || status === "COMPETENT_AUTHORITY_APPROVAL") {
              targetStep = 7;
            } else {
              targetStep = 2;
            }
            updateStep(targetStep, cycle.id);
          }
        }
      } catch (e) {
        console.error("Failed to load active cycle details", e);
      } finally {
        setLoadingCandidates(false);
      }
    };

    loadActiveCycle();
  }, [urlCycleId, urlStep]);

  // Step 2: Calculate State
  const [isCalculating, setIsCalculating] = useState(false);
  const [calcResult, setCalcResult] = useState<any>(null);
  const [candidatesList, setCandidatesList] = useState<any[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });
  // Step 3: Clearances State
  const [isSettingDar, setIsSettingDar] = useState(false);
  const [isSettingVigilance, setIsSettingVigilance] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [clearanceResult, setClearanceResult] = useState<any>(null);
  const [pvrClearedList, setPvrClearedList] = useState<any[]>([]);
  const [loadingPvrCleared, setLoadingPvrCleared] = useState<boolean>(false);
  const [isSavingPvr, setIsSavingPvr] = useState(false);
  const [darUploaded, setDarUploaded] = useState(false);
  const [isUploadingDar, setIsUploadingDar] = useState(false);
  const [vigilanceUploaded, setVigilanceUploaded] = useState(false);
  const [isUploadingVigilance, setIsUploadingVigilance] = useState(false);
  const [selectedDarCandidate, setSelectedDarCandidate] = useState<any | null>(null);
  const darFileInputRef = useRef<HTMLInputElement>(null);
  const vigilanceFileInputRef = useRef<HTMLInputElement>(null);
  // Step 2: PVR Clearance & Bulk Update State
  const [selectedPvrRowKeys, setSelectedPvrRowKeys] = useState<any[]>([]);
  const [bulkPvrStatus, setBulkPvrStatus] = useState<string>("Clear");
  const [pvrSearchTerm, setPvrSearchTerm] = useState<string>("");
  const [pvrStatusFilter, setPvrStatusFilter] = useState<string>("ALL");
  const [pvrRemarksErrors, setPvrRemarksErrors] = useState<Record<string, boolean>>({});
  const [selectedPvrRecord, setSelectedPvrRecord] = useState<any | null>(null);
  const [isSyncingPvrFromApi, setIsSyncingPvrFromApi] = useState<boolean>(false);
  const [wrSearchTerm, setWrSearchTerm] = useState<string>("");
  const [darSearchTerm, setDarSearchTerm] = useState<string>("");

  // Step 6: Vigilance Clearance & Bulk Update State
  const [selectedVigRowKeys, setSelectedVigRowKeys] = useState<any[]>([]);
  const [bulkVigStatus, setBulkVigStatus] = useState<string>("No Vigilance Case");
  const [bulkVigRemarks, setBulkVigRemarks] = useState<string>("");
  const [vigSearchTerm, setVigSearchTerm] = useState<string>("");
  const [vigStatusFilter, setVigStatusFilter] = useState<string>("ALL");
  const [vigRemarksErrors, setVigRemarksErrors] = useState<Record<string, boolean>>({});

  // Step 3: WR Grading State
  const [wrUploaded, setWrUploaded] = useState(false);
  const [isUploadingWr, setIsUploadingWr] = useState(false);
  const [isDownloadingAppraisalPeriods, setIsDownloadingAppraisalPeriods] = useState(false);
  const [wrClearedList, setWrClearedList] = useState<any[]>([]);
  const [loadingWrCleared, setLoadingWrCleared] = useState<boolean>(false);
  const wrFileInputRef = useRef<HTMLInputElement>(null);

  // Modular Progress Modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [modalText, setModalText] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressStatus, setProgressStatus] = useState("Processing...");

  const runWizardStep = async (
    text: string,
    action: () => Promise<void>
  ) => {
    setModalText(text);
    setProgressPercent(0);
    setProgressStatus("Processing...");
    setShowProgressModal(true);

    let progress = 0;
    const intervalId = setInterval(() => {
      progress += 1;
      if (progress >= 95) {
        progress = 95;
        clearInterval(intervalId);
      }
      setProgressPercent(progress);
    }, 800);

    try {
      await action();
      clearInterval(intervalId);
      setProgressPercent(100);
      setProgressStatus("Completed!");
      await new Promise((resolve) => setTimeout(resolve, 800));
    } catch (err) {
      clearInterval(intervalId);
      throw err;
    } finally {
      setShowProgressModal(false);
    }
  };

  const goPrev = () => {
    const prevStep = Math.max(1, step - 1);
    updateStep(prevStep);
  };

  const toggleGrade = (val: string) => {
    setGrades(prev =>
      prev.includes(val) ? prev.filter(g => g !== val) : [...prev, val]
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (gradeDropdownRef.current && !gradeDropdownRef.current.contains(e.target as Node)) {
        setGradeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setGrades([]);
    setGradeDropdownOpen(false);
    setGradesMaster([]);

    if (!employeeType) return;
    let cancelled = false;

    const fetchGrades = async () => {
      setLoadingGrades(true);
      try {
        const normalizedEmployeeType = employeeType === "Executive"
          ? "EXECUTIVE"
          : "NON-EXECUTIVE";
        const response = await Api.get(
          API_URLS.PROMOTION.PAY_SCALE_GRADE_MASTER,
          [{ employeeType: normalizedEmployeeType }],
          true
        );
        const responseData = response?.data?.data ?? response?.data ?? response;
        const list = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData?.items)
            ? responseData.items
            : [];
        if (!cancelled) setGradesMaster(list);
      } catch (e) {
        console.error("Failed to fetch pay grade scale master", e);
        if (!cancelled) {
          toast.error("Failed to load pay scales for the selected employee type");
        }
      } finally {
        if (!cancelled) setLoadingGrades(false);
      }
    };
    fetchGrades();
    return () => {
      cancelled = true;
    };
  }, [employeeType]);

  const formatEmploymentTypeText = (text: any): string => {
    if (!text) return "";
    if (typeof text !== "string") return String(text);
    if (text === "all") return "all";
    if (text.includes("_") || text === text.toUpperCase()) {
      return text
        .toLowerCase()
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
    return text;
  };

  const getEmploymentTypeLabel = (item: any) => {
    if (!item) return "";
    let raw = "";
    if (typeof item === "string") {
      raw = item;
    } else {
      raw =
        item?.label ||
        item?.name ||
        item?.employmentType ||
        item?.employment_type ||
        item?.employeeType ||
        item?.employee_type ||
        item?.value ||
        item?.type ||
        "";
    }
    return formatEmploymentTypeText(raw);
  };

  const getEmploymentTypeValue = (item: any) => {
    return getEmploymentTypeLabel(item);
  };

  const getPayScaleValue = (grade: any) =>
    grade?.empPayscale ||
    grade?.empPayScale ||
    grade?.payScale ||
    grade?.pay_scale ||
    grade?.payScaleName ||
    grade?.name ||
    grade?.grade ||
    "";

  const allPayScales = React.useMemo(() => {
    return Array.from(
      new Set(
        gradesMaster
          .map((g: any) => getPayScaleValue(g))
          .filter(Boolean)
      )
    );
  }, [gradesMaster]);

  const isAllGradesSelected =
    allPayScales.length > 0 &&
    allPayScales.every((ps: string) => grades.includes(ps));

  const toggleSelectAllGrades = () => {
    if (isAllGradesSelected) {
      setGrades([]);
    } else {
      setGrades(allPayScales);
    }
  };

  const mapCandidate = (candidate: any, index: number) => {
    const empIdStr = candidate.empId != null ? String(candidate.empId) : "—";
    const latestRec = getLatestPvrRecord(candidate) || candidate.latestPvrRecord || candidate.latestpvrrecord || null;
    const { pvrStatus, pvrRemarks } = mapPvrRecord(candidate);
    const wrFinalStatusVal = candidate.wrFinalStatus ?? candidate.wrFinal ?? "PENDING";

    return {
      ...candidate,
      serialNo: index + 1,
      srNo: index + 1,
      id: candidate.id ?? candidate.empId ?? index + 1,
      key: candidate.id != null ? String(candidate.id) : (candidate.empId != null ? String(candidate.empId) : String(index + 1)),
      empId: empIdStr,
      empNo: empIdStr,
      empName: candidate.empName ?? candidate.name ?? candidate.employeeName ?? "—",
      name: candidate.empName ?? candidate.name ?? candidate.employeeName ?? "—",
      dateOfJoining: candidate.dateOfJoining ?? candidate.date_of_joining ?? candidate.doj ?? null,
      doj: candidate.dateOfJoining ?? candidate.date_of_joining ?? candidate.doj ?? null,
      confirmationDate: candidate.confirmationDate ?? candidate.confirmation_date ?? null,
      adjustedConfirmationDate:
        candidate.adjustedConfirmationDate ??
        candidate.adjusted_confirmation_date ??
        null,
      adjustmentDaysLwp:
        candidate.adjustmentDaysLwp ??
        candidate.adjustment_days_lwp ??
        candidate.adjustedLwpDays ??
        candidate.adjusted_lwp_days ??
        candidate.lwpDays ??
        candidate.lwp_days ??
        candidate.lwp ??
        0,
      lwpDays:
        candidate.adjustmentDaysLwp ??
        candidate.adjustment_days_lwp ??
        candidate.adjustedLwpDays ??
        candidate.adjusted_lwp_days ??
        candidate.lwpDays ??
        candidate.lwp_days ??
        candidate.lwp ??
        0,
      lwp:
        candidate.adjustmentDaysLwp ??
        candidate.adjustment_days_lwp ??
        candidate.adjustedLwpDays ??
        candidate.adjusted_lwp_days ??
        candidate.lwpDays ??
        candidate.lwp_days ??
        candidate.lwp ??
        0,
      adjustmentDaysSuspension:
        candidate.adjustmentDaysSuspension ??
        candidate.adjustment_days_suspension ??
        candidate.adjustedSuspensionDays ??
        candidate.suspensionDays ??
        candidate.suspension_days ??
        candidate.suspension ??
        0,
      suspensionDays:
        candidate.adjustmentDaysSuspension ??
        candidate.adjustment_days_suspension ??
        candidate.adjustedSuspensionDays ??
        candidate.suspensionDays ??
        candidate.suspension_days ??
        candidate.suspension ??
        0,
      suspension:
        candidate.adjustmentDaysSuspension ??
        candidate.adjustment_days_suspension ??
        candidate.adjustedSuspensionDays ??
        candidate.suspensionDays ??
        candidate.suspension_days ??
        candidate.suspension ??
        0,
      adjustmentDaysEol:
        candidate.adjustmentDaysEol ??
        candidate.adjustment_days_eol ??
        candidate.adjustedEolDays ??
        candidate.eolDays ??
        candidate.eol_days ??
        candidate.eol ??
        0,
      eolDays:
        candidate.adjustmentDaysEol ??
        candidate.adjustment_days_eol ??
        candidate.adjustedEolDays ??
        candidate.eolDays ??
        candidate.eol_days ??
        candidate.eol ??
        0,
      eol:
        candidate.adjustmentDaysEol ??
        candidate.adjustment_days_eol ??
        candidate.adjustedEolDays ??
        candidate.eolDays ??
        candidate.eol_days ??
        candidate.eol ??
        0,
      adjustmentDaysDiesNon:
        candidate.adjustmentDaysDiesNon ??
        candidate.adjustment_days_dies_non ??
        candidate.adjustedDiesNonDays ??
        candidate.diesNonDays ??
        candidate.dies_non_days ??
        candidate.diesNon ??
        0,
      diesNonDays:
        candidate.adjustmentDaysDiesNon ??
        candidate.adjustment_days_dies_non ??
        candidate.adjustedDiesNonDays ??
        candidate.diesNonDays ??
        candidate.dies_non_days ??
        candidate.diesNon ??
        0,
      diesNon:
        candidate.adjustmentDaysDiesNon ??
        candidate.adjustment_days_dies_non ??
        candidate.adjustedDiesNonDays ??
        candidate.diesNonDays ??
        candidate.dies_non_days ??
        candidate.diesNon ??
        0,
      pvrStatusText: pvrStatus,
      pvrStatus: pvrStatus,
      pvr: pvrStatus,
      pvrRemarks: pvrRemarks,
      remarks: pvrRemarks,
      latestPvrRecord: latestRec,
      wrFinalStatus: wrFinalStatusVal,
      wrFinal: wrFinalStatusVal,
    };
  };

  const handleInitiate = async () => {
    if (!employeeType) {
      toast.error("Please select an Employee Type");
      return;
    }
    if (grades.length === 0) {
      toast.error("Please select at least one Pay Scale");
      return;
    }
    if (!confirmationDueOn) {
      toast.error("Please select Confirmation Due On date");
      return;
    }
    setIsInitiating(true);
    try {
      await runWizardStep("Syncing and Initiating Probation Cycle...", async () => {
        const formattedEmpType = formatEmploymentTypeText(employmentType || "all");
        const payload = {
          financial_year: financialYear,
          grades,
          confirmation_due_on: confirmationDueOn,
          probation_due_date: confirmationDueOn,
          probation_from_date: confirmationDueOn,
          probation_to_date: confirmationDueOn,
          employee_range: employeeRange || "all",
          employment_type: formattedEmpType,
          employee_type: employeeType || undefined,
          // CamelCase compatibility
          financialYear,
          confirmationDueOn,
          probationDueDate: confirmationDueOn,
          probationFromDate: confirmationDueOn,
          probationToDate: confirmationDueOn,
          employeeRange: employeeRange || "all",
          employmentType: formattedEmpType,
          employeeType
        };
        const response = await Api.post(API_URLS.PROBATION.INITIATE, payload, true);
        const resData = response?.data || response;
        const innerData = resData?.data || resData;

        // Extract cycle details robustly
        const createdCycle = innerData?.cycle || resData?.cycle || innerData?.data || innerData || resData;
        setInitiatedCycle(createdCycle);

        const candidates = innerData?.candidates || resData?.candidates || (Array.isArray(innerData) ? innerData : []);
        const mapped = candidates.map((c: any, idx: number) => ({
          key: c.empId || c.emp_id || c.employeeId || c.employee_id ? String(c.empId || c.emp_id || c.employeeId || c.employee_id) : String(idx),
          srNo: idx + 1,
          empNo: c.empId || c.emp_id || c.employeeId || c.employee_id || c.empNo || c.emp_no,
          name: c.empName || c.emp_name || c.name || c.employeeName || c.employee_name,
          designation: c.designation || c.designationId || c.designation_name || "N/A",
          doj: c.dateOfJoining || c.date_of_joining || c.doj || "N/A",
          probationYears: c.configuredProbationYears || c.probationYears || c.probation_years || 1,
          confirmationDate: c.confirmationDate || c.confirmation_date || c.adjustedConfirmationDate || c.adjusted_confirmation_date || c.probationDueDate || c.probation_due_date || c.confirmationDueOn || c.confirmation_due_on || "N/A"
        }));
        setCandidatesList(mapped);

        const id = createdCycle?.id || createdCycle?.cycleId || createdCycle?.cycle_id || resData?.id || innerData?.id;
        if (id) {
          toast.success("Probation Cycle Initiated Successfully!");
          updateStep(2, id);
        } else {
          throw new Error("Cycle initiated but no ID returned from API");
        }
      });
    } catch (e: any) {
      console.error("Initiation failed", e);
      toast.error("Failed to initiate cycle: " + Api.getErrorMessage(e));
    } finally {
      setIsInitiating(false);
    }
  };

  const handleCalculate = async () => {
    if (!cycleId) {
      toast.error("Cycle ID is missing. Please restart initiation.");
      return;
    }
    setIsCalculating(true);
    try {
      await runWizardStep("Calculating Probation Candidacy...", async () => {
        const payload = {
          cycleId: Number(cycleId),
          page: 1,
          limit: 5000
        };
        const response = await Api.post(API_URLS.PROBATION.CALCULATE_PROBATION, payload, true);
        const resData = response?.data || response;
        setCalcResult(resData);
        toast.success("Probation candidacy calculated successfully!");

        const innerPayload = response?.data?.data;
        const candidates = Array.isArray(innerPayload?.data)
          ? innerPayload.data
          : Array.isArray(innerPayload)
            ? innerPayload
            : Array.isArray(response?.data?.data)
              ? response.data.data
              : Array.isArray(response?.data)
                ? response.data
                : [];

        const mapped = candidates.map((c: any, idx: number) => mapCandidate(c, idx));
        setPvrClearedList(mapped);
        updateStep(4);
      });
    } catch (e: any) {
      console.error("Calculation failed", e);
      toast.error("Candidacy calculation failed: " + Api.getErrorMessage(e));
    } finally {
      setIsCalculating(false);
    }
  };

  const formatDateDMY = (val: any) => {
    if (!val || val === "N/A" || val === "null" || val === "undefined" || val === "-") return "—";
    const str = String(val).trim();
    if (str.includes("T")) {
      const ymd = str.split("T")[0];
      const parts = ymd.split("-");
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    if (str.includes("-")) {
      const parts = str.split("-");
      if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}/${parts[1]}/${parts[0]}`;
      if (parts.length === 3 && parts[2].length === 4) return str.replace(/-/g, "/");
    }
    if (str.includes("/")) {
      return str;
    }
    return str;
  };

  const formatDate = (val: string) => {
    return formatDateDMY(val);
  };

  useEffect(() => {
    if (!cycleId || step !== 3) return;

    const fetchCandidates = async () => {
      setLoadingCandidates(true);
      try {
        const response = await Api.post(
          API_URLS.PROBATION.PVR_CLEARED,
          { cycleId: Number(cycleId) },
          true
        );
        const resData = response?.data || response;
        const innerData = resData?.data || resData;

        // Extract list of candidates
        const list = Array.isArray(innerData)
          ? innerData
          : (
            innerData?.candidates ||
            innerData?.pvrClearedCandidates ||
            innerData?.records ||
            innerData?.items ||
            []
          );

        const mapped = list.map((c: any, idx: number) => {
          const latestRec = getLatestPvrRecord(c);
          const { pvrStatus, pvrRemarks } = mapPvrRecord(c);
          const empIdVal = String(c.empId || c.employee?.empId || c.empNo || "").trim();

          return {
            key: empIdVal || String(idx),
            srNo: idx + 1,
            empNo: empIdVal,
            name: c.empName || c.employee?.empName || c.employee?.name,
            designation: c.designation || c.designationId || "N/A",
            doj: c.dateOfJoining || "N/A",
            probationYears: c.configuredProbationYears || 1,
            confirmationDate: c.confirmationDate || c.confirmation_date || c.adjustedConfirmationDate || c.adjusted_confirmation_date || c.probationDueDate || c.probation_due_date || c.confirmationDueOn || c.confirmation_due_on || "N/A",
            pvr: pvrStatus,
            pvrStatus: pvrStatus,
            pvrRemarks: pvrRemarks,
            remarks: pvrRemarks,
            latestPvrRecord: latestRec
          };
        });
        setCandidatesList(mapped);

      } catch (e) {
        console.error("Failed to fetch PVR-cleared candidates for Step 3", e);
        setCandidatesList([]);
      } finally {
        setLoadingCandidates(false);
      }
    };

    fetchCandidates();
  }, [cycleId, step]);

  const fetchDataForStep = async (targetStep: number) => {
    if (!cycleId) return;
    if (targetStep === 2 || targetStep === 4) setLoadingPvrCleared(true);
    if (targetStep === 5 || targetStep === 6) setLoadingWrCleared(true);
    try {
      const endpoint = targetStep === 2
        ? API_URLS.PROBATION.CANDIDATES
        : targetStep === 4
          ? API_URLS.PROBATION.CALCULATE_PROBATION
          : API_URLS.PROBATION.WR_SUITABLE;
      const response = await Api.post(
        endpoint,
        { cycleId: Number(cycleId), page: 1, limit: 5000 },
        true
      );
      const resData = response?.data || response;
      const innerData = resData?.data || resData;

      if (targetStep === 4) {
        const payload = response?.data?.data;
        const candidates = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : Array.isArray(response?.data?.data)
              ? response.data.data
              : Array.isArray(response?.data)
                ? response.data
                : [];

        const mapped = candidates.map((c: any, idx: number) => mapCandidate(c, idx));
        setPvrClearedList(mapped);

        const hasWr = candidates.some((c: any) => c.wrFinalStatus && c.wrFinalStatus !== 'PENDING' && c.wrFinalStatus !== 'NO_DATA');
        if (hasWr) setWrUploaded(true);
        return;
      }

      let rawList: any[] = [];
      if (Array.isArray(innerData)) {
        rawList = innerData;
      } else if (Array.isArray(resData)) {
        rawList = resData;
      } else if (innerData && typeof innerData === "object") {
        const summaryObj = innerData.summary || resData?.summary || innerData;
        const candidateField = (
          innerData.candidates ||
          innerData.calculatedCandidates ||
          innerData.eligibleCandidates ||
          innerData.eligibleList ||
          innerData.masterData ||
          innerData.pvrClearedCandidates ||
          innerData.wrSuitableCandidates ||
          innerData.suitableCandidates ||
          innerData.records ||
          innerData.items ||
          summaryObj?.candidates ||
          summaryObj?.calculatedCandidates ||
          summaryObj?.eligibleCandidates ||
          summaryObj?.eligibleList ||
          summaryObj?.masterData ||
          summaryObj?.items ||
          summaryObj?.records
        );

        if (Array.isArray(candidateField) && candidateField.length > 0) {
          rawList = candidateField;
        } else if (summaryObj && typeof summaryObj === "object") {
          const eligible = Array.isArray(summaryObj.eligibleList) ? summaryObj.eligibleList : (Array.isArray(summaryObj.eligible) ? summaryObj.eligible : []);
          const listA = Array.isArray(summaryObj.listA_PvrIssues) ? summaryObj.listA_PvrIssues : (Array.isArray(summaryObj.listA) ? summaryObj.listA : []);
          const listB = Array.isArray(summaryObj.listB_ProbationNotCompleted) ? summaryObj.listB_ProbationNotCompleted : (Array.isArray(summaryObj.listB) ? summaryObj.listB : []);
          const listC = Array.isArray(summaryObj.listC_UnsuitableWrGrading) ? summaryObj.listC_UnsuitableWrGrading : (Array.isArray(summaryObj.listC) ? summaryObj.listC : []);
          const listD = Array.isArray(summaryObj.listD_DarCases) ? summaryObj.listD_DarCases : (Array.isArray(summaryObj.listD) ? summaryObj.listD : []);
          const listE = Array.isArray(summaryObj.listE_VigilanceCases) ? summaryObj.listE_VigilanceCases : (Array.isArray(summaryObj.listE) ? summaryObj.listE : []);

          if (Array.isArray(summaryObj.masterData) && summaryObj.masterData.length > 0) {
            rawList = summaryObj.masterData;
          } else {
            rawList = [...listA, ...listB, ...listC, ...listD, ...listE, ...eligible];
          }
        }
      }

      if (!Array.isArray(rawList)) {
        rawList = [];
      }

      const mapped = rawList.map((c: any, idx: number) => {
        const emp = c.employee || c.candidate || c;
        const empIdVal = String(emp.empId || emp.emp_id || emp.empNo || emp.emp_no || c.empId || c.emp_id || c.empNo || c.emp_no || "").trim();
        const nameVal = emp.empName || emp.emp_name || emp.employeeName || emp.name || c.empName || c.emp_name || c.employeeName || c.name;
        const desigVal = emp.designation || emp.designationId || c.designation || c.designationId || "N/A";
        const dojVal = emp.dateOfJoining || emp.date_of_joining || c.dateOfJoining || c.date_of_joining || "N/A";
        const probYears = emp.configuredProbationYears || emp.probationYears || c.configuredProbationYears || c.probationYears || 1;
        const confDate = emp.confirmationDate || emp.confirmation_date || c.confirmationDate || c.confirmation_date || c.probationDueDate || c.probation_due_date || c.confirmationDueOn || c.confirmation_due_on || "N/A";
        const adjConfDate = c.adjustedConfirmationDate || c.adjusted_confirmation_date || emp.adjustedConfirmationDate || emp.adjusted_confirmation_date || confDate;

        const latestRec = getLatestPvrRecord(c) || c.latestPvrRecord || c.latestpvrrecord || null;
        const { pvrStatus, pvrRemarks } = mapPvrRecord(c);

        return {
          ...c,
          key: empIdVal || String(idx),
          srNo: idx + 1,
          empNo: empIdVal,
          name: nameVal,
          designation: desigVal,
          doj: dojVal,
          probationYears: probYears,
          confirmationDate: confDate,
          adjustedConfirmationDate: adjConfDate,
          lwpDays: c.lwp_days ?? c.lwpDays ?? c.lwp ?? c.lwpDaysCount ?? 0,
          suspensionDays: c.suspension_days ?? c.suspensionDays ?? c.deemed_suspension ?? c.deemedSuspension ?? c.deemed_suspension_days ?? c.deemedSuspensionDays ?? c.suspension ?? 0,
          eolDays: c.eol_days ?? c.eolDays ?? c.eol ?? c.eol_days_count ?? 0,
          diesNonDays: c.dies_non_days ?? c.diesNonDays ?? c.dies_non ?? c.diesNon ?? c.dies_non_count ?? 0,
          pvr: pvrStatus,
          pvrStatus: pvrStatus,
          pvrRemarks: pvrRemarks,
          remarks: pvrRemarks,
          latestPvrRecord: latestRec,
          dar_evaluation: c.dar_evaluation || c.darEvaluation || null,
          darEvaluation: c.dar_evaluation || c.darEvaluation || null,
          darCleared: (c.dar_evaluation || c.darEvaluation)?.darCleared ?? (c.dar_evaluation || c.darEvaluation)?.dar_cleared ?? c.darCleared ?? c.dar_cleared ?? false,
          dar: (c.dar_evaluation || c.darEvaluation)?.status ?? (c.dar_evaluation || c.darEvaluation)?.darStatus ?? (c.dar_evaluation || c.darEvaluation)?.dar_status ?? c.darStatus ?? c.dar_status ?? "DAR CLEAR",
          darStatus: (c.dar_evaluation || c.darEvaluation)?.status ?? (c.dar_evaluation || c.darEvaluation)?.darStatus ?? (c.dar_evaluation || c.darEvaluation)?.dar_status ?? c.darStatus ?? c.dar_status ?? "DAR CLEAR",
          caseNo: (c.dar_evaluation || c.darEvaluation)?.caseNo ?? (c.dar_evaluation || c.darEvaluation)?.case_no ?? c.caseNo ?? c.case_no ?? null,
          chargeSheetIssuedDate: (c.dar_evaluation || c.darEvaluation)?.chargeSheetIssuedDate ?? (c.dar_evaluation || c.darEvaluation)?.charge_sheet_issued_date ?? c.chargeSheetIssuedDate ?? c.charge_sheet_issued_date ?? null,
          penaltyDescription: (c.dar_evaluation || c.darEvaluation)?.penaltyDescription ?? (c.dar_evaluation || c.darEvaluation)?.penalty_description ?? c.penaltyDescription ?? c.penalty_description ?? null,
          dateOfAction: (c.dar_evaluation || c.darEvaluation)?.dateOfAction ?? (c.dar_evaluation || c.darEvaluation)?.date_of_action ?? c.dateOfAction ?? c.date_of_action ?? null,
          penaltyStartDate: (c.dar_evaluation || c.darEvaluation)?.penaltyStartDate ?? (c.dar_evaluation || c.darEvaluation)?.penalty_start_date ?? c.penaltyStartDate ?? c.penalty_start_date ?? null,
          penaltyEndDate: (c.dar_evaluation || c.darEvaluation)?.penaltyEndDate ?? (c.dar_evaluation || c.darEvaluation)?.penalty_end_date ?? c.penaltyEndDate ?? c.penalty_end_date ?? null,
          vigilance: c.vigilanceStatus || c.vigilance || "No Vigilance Case",
          vigilanceStatus: c.vigilanceStatus || c.vigilance || "No Vigilance Case",
          vigilanceRemarks: c.remarks || c.vigilanceRemarks || "",
          wrYear1: c.wrYear1,
          wrYear2: c.wrYear2,
          wrFinal: c.wrFinalStatus
        };
      });

      if (targetStep === 2) {
        setPvrClearedList(mapped);
      } else if (targetStep === 5 || targetStep === 6) {
        setWrClearedList(mapped);
      }

      // Check upload statuses
      const hasWr = rawList.some((c: any) => c.wrFinalStatus && c.wrFinalStatus !== 'PENDING' && c.wrFinalStatus !== 'NO_DATA');
      if (hasWr) setWrUploaded(true);

      const hasDar = rawList.some((c: any) => c.darStatus && c.darStatus !== 'PENDING' && c.darStatus !== 'NO_DATA');
      if (hasDar) setDarUploaded(true);

      const hasVig = rawList.some((c: any) => c.vigilanceStatus && c.vigilanceStatus !== 'PENDING' && c.vigilanceStatus !== 'NO_DATA');
      if (hasVig) setVigilanceUploaded(true);
    } catch (e) {
      console.error(`Failed to fetch data for step ${targetStep}`, e);
    } finally {
      if (targetStep === 2 || targetStep === 4) setLoadingPvrCleared(false);
      if (targetStep === 5 || targetStep === 6) setLoadingWrCleared(false);
    }
  };

  useEffect(() => {
    if (cycleId && (step === 2 || step === 4 || step === 5 || step === 6 || step === 7)) {
      fetchDataForStep(step);
    }
  }, [cycleId, step]);

  const pvrStatusCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    PVR_STATUS_OPTIONS.forEach((opt) => {
      counts[opt] = 0;
    });
    pvrClearedList.forEach((c: any) => {
      const s = normalizePvrStatus(c.pvr);
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [pvrClearedList]);

  const filteredPvrList = React.useMemo(() => {
    let list = pvrClearedList;
    if (pvrStatusFilter && pvrStatusFilter !== "ALL") {
      list = list.filter((c: any) => mapPvrRecord(c).pvrStatus === pvrStatusFilter);
    }
    if (pvrSearchTerm.trim()) {
      list = list.filter((c: any) => matchCandidateRecord(c, pvrSearchTerm));
    }
    return list;
  }, [pvrClearedList, pvrStatusFilter, pvrSearchTerm]);

  const filteredCandidatesList = React.useMemo(() => {
    let list = candidatesList;
    if (pvrStatusFilter && pvrStatusFilter !== "ALL") {
      list = list.filter((c: any) => mapPvrRecord(c).pvrStatus === pvrStatusFilter);
    }
    if (pvrSearchTerm.trim()) {
      list = list.filter((c: any) => matchCandidateRecord(c, pvrSearchTerm));
    }
    return list;
  }, [candidatesList, pvrStatusFilter, pvrSearchTerm]);

  const handleExportPvrExcel = () => {
    try {
      if (pvrClearedList.length === 0) {
        toast.warning("No candidate data available to export");
        return;
      }

      const headers = [
        "Sr. No.",
        "Employee No",
        "Employee Name",
        "Designation",
        "Date of Joining",
        "Probation Period",
        "Confirmation Date",
        "PVR Status"
      ];

      const rows = pvrClearedList.map((item: any, idx: number) => {
        return [
          idx + 1,
          item.empNo || "",
          item.name || "",
          item.designation || "",
          formatDate(item.doj),
          `${item.probationYears || 1} Year(s)`,
          formatDate(item.confirmationDate),
          normalizePvrStatus(item.pvr),
        ];
      });

      const escapeCsvCell = (value: any) =>
        `"${String(value ?? "").replace(/"/g, '""')}"`;

      const csvContent = "\uFEFF" + [
        headers.map(escapeCsvCell).join(","),
        ...rows.map((row: any[]) => row.map(escapeCsvCell).join(",")),
      ].join("\r\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `PVR_Clearance_Cycle_${cycleId || "Active"}_${financialYear || "FY"}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("PVR candidate table exported to Excel successfully!");
    } catch (err) {
      console.error("Export failed", err);
      toast.error("Failed to export table data");
    }
  };

  const handleBulkApplyPvrStatus = (target: "selected" | "all") => {
    if (!bulkPvrStatus) {
      toast.warning("Please select a PVR status to apply.");
      return;
    }
    if (target === "selected") {
      if (selectedPvrRowKeys.length === 0) {
        toast.warning("Please select at least one candidate using the checkboxes.");
        return;
      }
      setPvrClearedList((prev) =>
        prev.map((c: any) =>
          selectedPvrRowKeys.includes(c.key)
            ? { ...c, pvr: bulkPvrStatus, pvrStatus: bulkPvrStatus }
            : c
        )
      );
      toast.success(`Updated PVR status to "${bulkPvrStatus}" for ${selectedPvrRowKeys.length} selected candidate(s).`);
    } else {
      if (pvrClearedList.length === 0) {
        toast.warning("No candidates available to update.");
        return;
      }
      setPvrClearedList((prev) =>
        prev.map((c: any) => ({ ...c, pvr: bulkPvrStatus, pvrStatus: bulkPvrStatus }))
      );
      toast.success(`Updated PVR status to "${bulkPvrStatus}" for all ${pvrClearedList.length} candidate(s).`);
    }
  };

  const handleQuickSetAllPvr = (status: string) => {
    if (pvrClearedList.length === 0) return;
    setPvrClearedList((prev) =>
      prev.map((c: any) => ({ ...c, pvr: status, pvrStatus: status }))
    );
    toast.success(`Set PVR status to "${status}" for all ${pvrClearedList.length} candidate(s).`);
  };

  // Step 6: Vigilance Clearance Handlers & Filters
  const vigStatusCounts = React.useMemo(() => {
    const counts: Record<string, number> = { Clear: 0, Pending: 0 };
    wrClearedList.forEach((c: any) => {
      const s = normalizeVigilanceStatus(c.vigilance);
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [wrClearedList]);

  const filteredVigList = React.useMemo(() => {
    let list = wrClearedList;
    if (vigStatusFilter && vigStatusFilter !== "ALL") {
      list = list.filter((c: any) => normalizeVigilanceStatus(c.vigilance) === vigStatusFilter);
    }
    if (vigSearchTerm.trim()) {
      list = list.filter((c: any) => matchCandidateRecord(c, vigSearchTerm));
    }
    return list;
  }, [wrClearedList, vigStatusFilter, vigSearchTerm]);

  const filteredWrStep4List = React.useMemo(() => {
    if (!wrSearchTerm || !wrSearchTerm.trim()) return pvrClearedList;
    return pvrClearedList.filter((c: any) => matchCandidateRecord(c, wrSearchTerm));
  }, [pvrClearedList, wrSearchTerm]);

  const filteredDarList = React.useMemo(() => {
    if (!darSearchTerm || !darSearchTerm.trim()) return wrClearedList;
    return wrClearedList.filter((c: any) => matchCandidateRecord(c, darSearchTerm));
  }, [wrClearedList, darSearchTerm]);

  const handleExportVigilanceExcel = () => {
    try {
      if (wrClearedList.length === 0) {
        toast.warning("No candidate data available to export");
        return;
      }

      const headers = [
        "Sr. No.",
        "Employee No",
        "Employee Name",
        "Designation",
        "Date of Joining",
        "PVR Status",
        "PVR Remarks",
        "WR Final Status",
        "DAR Status",
        "Vigilance Status",
        "Vigilance Remarks"
      ];

      const rows = wrClearedList.map((item: any, idx: number) => {
        return [
          idx + 1,
          item.empNo || "",
          item.name || "",
          item.designation || "",
          formatDate(item.doj),
          normalizePvrStatus(item.pvr),
          item.pvrRemarks || "",
          item.wrFinal || "SUITABLE",
          item.dar || "CLEAR",
          normalizeVigilanceStatus(item.vigilance),
          item.vigilanceRemarks || ""
        ];
      });

      const escapeCsvCell = (value: any) =>
        `"${String(value ?? "").replace(/"/g, '""')}"`;

      const csvContent = "\uFEFF" + [
        headers.map(escapeCsvCell).join(","),
        ...rows.map((row: any[]) => row.map(escapeCsvCell).join(",")),
      ].join("\r\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `Vigilance_Clearance_Cycle_${cycleId || "Active"}_${financialYear || "FY"}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Vigilance candidate table exported to Excel successfully!");
    } catch (err) {
      console.error("Export failed", err);
      toast.error("Failed to export table data");
    }
  };

  const handleBulkApplyVigStatus = (target: "selected" | "all") => {
    if (!bulkVigStatus) {
      toast.warning("Please select a Vigilance status to apply.");
      return;
    }
    const remarksToApply = bulkVigRemarks.trim();
    if (target === "selected") {
      if (selectedVigRowKeys.length === 0) {
        toast.warning("Please select at least one candidate using the checkboxes.");
        return;
      }
      setWrClearedList((prev) =>
        prev.map((c: any) => {
          if (selectedVigRowKeys.includes(c.key)) {
            const nextRemark = remarksToApply || c.vigilanceRemarks || "";
            return {
              ...c,
              vigilance: bulkVigStatus,
              vigilanceStatus: bulkVigStatus,
              vigilanceRemarks: nextRemark
            };
          }
          return c;
        })
      );
      // Clear error highlights for selected keys
      setVigRemarksErrors((prev) => {
        const next = { ...prev };
        selectedVigRowKeys.forEach((k) => delete next[k]);
        return next;
      });
      toast.success(`Updated Vigilance status to "${bulkVigStatus}" for ${selectedVigRowKeys.length} selected candidate(s).`);
    } else {
      if (wrClearedList.length === 0) {
        toast.warning("No candidates available to update.");
        return;
      }
      setWrClearedList((prev) =>
        prev.map((c: any) => {
          const nextRemark = remarksToApply || c.vigilanceRemarks || "";
          return {
            ...c,
            vigilance: bulkVigStatus,
            vigilanceStatus: bulkVigStatus,
            vigilanceRemarks: nextRemark
          };
        })
      );
      setVigRemarksErrors({});
      toast.success(`Updated Vigilance status to "${bulkVigStatus}" for all ${wrClearedList.length} candidate(s).`);
    }
  };

  const handleQuickSetAllVig = (status: string) => {
    if (wrClearedList.length === 0) return;
    setWrClearedList((prev) =>
      prev.map((c: any) => ({
        ...c,
        vigilance: status,
        vigilanceStatus: status,
        vigilanceRemarks: c.vigilanceRemarks || ""
      }))
    );
    setVigRemarksErrors({});
    toast.success(`Set Vigilance status to "${status}" for all ${wrClearedList.length} candidate(s).`);
  };

  const handleSaveVigilanceStatuses = async () => {
    if (!cycleId) {
      toast.error("Cycle ID is missing.");
      return;
    }
    if (wrClearedList.length === 0) {
      toast.warning("No candidates available for Vigilance clearance.");
      return;
    }

    // Remarks are mandatory for candidates with "Vigilance Initiated" or "Penalty Awarded" status
    const missingRemarks = wrClearedList.filter(
      (c: any) => {
        const s = normalizeVigilanceStatus(c.vigilance);
        return (s === "Vigilance Initiated" || s === "Penalty Awarded") &&
          (!c.vigilanceRemarks || !String(c.vigilanceRemarks).trim());
      }
    );

    if (missingRemarks.length > 0) {
      const errMap: Record<string, boolean> = {};
      missingRemarks.forEach((c: any) => {
        errMap[c.key] = true;
      });
      setVigRemarksErrors(errMap);
      toast.error(`Please enter remarks for ${missingRemarks.length} candidate(s) with Vigilance Initiated or Penalty Awarded status.`);
      return;
    }

    setVigRemarksErrors({});
    setIsSettingVigilance(true);
    try {
      const payload = {
        cycleId: Number(cycleId),
        candidates: wrClearedList.map((candidate: any) => {
          const s = normalizeVigilanceStatus(candidate.vigilance);
          const isClear = s === "No Vigilance Case";
          return {
            empId: String(candidate.empNo),
            vigilanceStatus: isClear ? "CLEAR" : s === "Penalty Awarded" ? "PENALTY_AWARDED" : "VIGILANCE_INITIATED",
            vigilanceRemarks: String(candidate.vigilanceRemarks || "").trim(),
          };
        }),
      };
      const response = await Api.post(
        API_URLS.PROBATION.UPDATE_VIGILANCE_STATUS,
        payload,
        true
      );
      setClearanceResult(response?.data || response);
      toast.success("Vigilance statuses updated successfully!");
      updateStep(7);
    } catch (e: any) {
      console.error("Vigilance status update failed", e);
      toast.error("Vigilance status update failed: " + Api.getErrorMessage(e));
    } finally {
      setIsSettingVigilance(false);
    }
  };

  const [isRefreshingPvr, setIsRefreshingPvr] = useState(false);

  const handleRefreshPvrStatus = async () => {
    if (!cycleId) {
      toast.error("Cycle ID is missing.");
      return;
    }
    if (isRefreshingPvr) return;

    setIsRefreshingPvr(true);
    try {
      const candidatesToRefresh = pvrClearedList.length > 0 ? pvrClearedList : candidatesList;
      const candidatesPayload = candidatesToRefresh.map((c: any) => ({
        empId: String(c.empNo || c.empId || c.emp_id || c.key || "").trim()
      }));

      const payload = {
        cycleId: Number(cycleId),
        candidates: candidatesPayload
      };

      const response = await Api.post(API_URLS.PROBATION.SET_PVR_STATUS, payload, true);
      const resData = response?.data || response;
      const innerData = resData?.data || resData;

      const returnedCandidates =
        innerData?.candidates ||
        resData?.candidates ||
        response?.data?.data?.candidates ||
        response?.data?.candidates ||
        innerData?.records ||
        [];

      if (Array.isArray(returnedCandidates)) {
        const returnedMap = new Map<string, any>();
        returnedCandidates.forEach((item: any) => {
          const rawId = item.empId || item.empl_id || item.emp_id || item.empNo || item.emp_no || item.latestPvrRecord?.empId || item.latestPvrRecord?.empl_id || item.latestPvrRecord?.emp_id;
          getEmpIdKeys(rawId).forEach((k) => returnedMap.set(k, item));
        });

        const updateListWithPvr = (prevList: any[]) =>
          prevList.map((candidate: any) => {
            const candidateKeys = getEmpIdKeys(candidate.empNo || candidate.empId || candidate.emp_id || candidate.key);
            const returnedItem = candidateKeys.map((k) => returnedMap.get(k)).find(Boolean);

            if (returnedItem) {
              const latestRec = getLatestPvrRecord(returnedItem) || returnedItem?.latestPvrRecord || returnedItem;
              const { pvrStatus, pvrRemarks } = mapPvrRecord(returnedItem);

              return {
                ...candidate,
                ...returnedItem,
                pvr: pvrStatus,
                pvrStatus: pvrStatus,
                pvrRemarks: pvrRemarks,
                remarks: pvrRemarks,
                latestPvrRecord: latestRec
              };
            }
            return candidate;
          });

        setPvrClearedList(updateListWithPvr);
        setCandidatesList(updateListWithPvr);

        toast.success("PVR statuses updated successfully.");
      } else {
        toast.error("Unable to refresh PVR statuses. Please try again.");
      }
    } catch (error: any) {
      console.error("PVR status refresh error:", error);
      toast.error("Unable to refresh PVR statuses. Please try again.");
    } finally {
      setIsRefreshingPvr(false);
    }
  };

  const isValidFile = (file: File) => {
    const fileName = file.name.toLowerCase();
    return (
      fileName.endsWith(".csv") ||
      fileName.endsWith(".xlsx") ||
      fileName.endsWith(".xls") ||
      file.type === "text/csv" ||
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel" ||
      file.type === "application/excel"
    );
  };

  const handleUploadWr = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isValidFile(file)) {
      toast.error("Please upload a valid Excel or CSV file (.xlsx, .xls, .csv)");
      return;
    }

    setIsUploadingWr(true);
    try {
      const token = tokenService.getToken();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("cycleId", String(cycleId));

      const response = await axios.post(
        API_URLS.PROBATION.UPLOAD_WR,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      const res: any = response;
      const resData = res?.data || res;
      const isSuccess = res?.success === true || res?.data?.success === true || resData?.success === true || response?.status === 200 || response?.status === 201 || res?.status === 200 || res?.status === 201;
      const successMsg = resData?.data?.message || resData?.message || res?.data?.message || res?.message || "WR grading status updated successfully!";

      if (isSuccess) {
        setWrUploaded(true);
        toast.success(successMsg);
        await fetchDataForStep(4);
      } else {
        toast.error("Failed to upload WR Grading CSV");
      }
    } catch (err: any) {
      console.error("WR upload error:", err);
      const errMsg = err?.response?.data?.message || err?.message || "Failed to upload WR Grading CSV";
      toast.error(errMsg);
    } finally {
      setIsUploadingWr(false);
      if (wrFileInputRef.current) {
        wrFileInputRef.current.value = "";
      }
    }
  };

  const handleUploadDar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isValidFile(file)) {
      toast.error("Please upload a valid Excel or CSV file (.xlsx, .xls, .csv)");
      return;
    }

    setIsUploadingDar(true);
    try {
      const token = tokenService.getToken();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("cycleId", String(cycleId));

      const response = await axios.post(
        API_URLS.PROBATION.UPLOAD_DAR,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      const res: any = response;
      const resData = res?.data || res;
      const isSuccess = res?.success === true || res?.data?.success === true || resData?.success === true || response?.status === 200 || response?.status === 201 || res?.status === 200 || res?.status === 201;
      const successMsg = resData?.data?.message || resData?.message || res?.data?.message || res?.message || "DAR status updated successfully!";

      if (isSuccess) {
        setDarUploaded(true);
        toast.success(successMsg);
        await fetchDataForStep(5);
      } else {
        toast.error("Failed to upload DAR CSV");
      }
    } catch (err: any) {
      console.error("DAR upload error:", err);
      const errMsg = err?.response?.data?.message || err?.message || "Failed to upload DAR CSV";
      toast.error(errMsg);
    } finally {
      setIsUploadingDar(false);
      if (darFileInputRef.current) {
        darFileInputRef.current.value = "";
      }
    }
  };

  const handleUploadVigilance = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isValidFile(file)) {
      toast.error("Please upload a valid Excel or CSV file (.xlsx, .xls, .csv)");
      return;
    }

    setIsUploadingVigilance(true);
    try {
      const token = tokenService.getToken();
      const formData = new FormData();
      formData.append("file", file);
      formData.append("cycleId", String(cycleId));

      const response = await axios.post(
        API_URLS.PROBATION.UPLOAD_VIGILANCE,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      const res: any = response;
      const resData = res?.data || res;
      const isSuccess = res?.success === true || res?.data?.success === true || resData?.success === true || response?.status === 200 || response?.status === 201 || res?.status === 200 || res?.status === 201;
      const successMsg = resData?.data?.message || resData?.message || res?.data?.message || res?.message || "Vigilance status updated successfully!";

      if (isSuccess) {
        setVigilanceUploaded(true);
        toast.success(successMsg);
        await fetchDataForStep(5);
      } else {
        toast.error("Failed to upload Vigilance CSV");
      }
    } catch (err: any) {
      console.error("Vigilance upload error:", err);
      const errMsg = err?.response?.data?.message || err?.message || "Failed to upload Vigilance CSV";
      toast.error(errMsg);
    } finally {
      setIsUploadingVigilance(false);
      if (vigilanceFileInputRef.current) {
        vigilanceFileInputRef.current.value = "";
      }
    }
  };

  const handleDownloadAppraisalPeriodsExcel = async () => {
    if (!cycleId) {
      toast.error("Cycle ID is missing");
      return;
    }

    setIsDownloadingAppraisalPeriods(true);
    try {
      const token = tokenService.getToken();
      const response = await axios.post(
        API_URLS.PROBATION.DOWNLOAD_APPRAISAL_PERIODS,
        { cycleId: Number(cycleId) },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          responseType: "blob"
        }
      );

      let fileName = `appraisal_periods_cycle_${cycleId}.xlsx`;
      const contentDisposition = response.headers?.["content-disposition"];
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";]+)"?/);
        if (match && match[1]) {
          fileName = match[1];
        }
      }

      const blob = new Blob([response.data], {
        type: response.headers?.["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("File downloaded successfully!");
    } catch (err: any) {
      console.error("Failed to download appraisal periods Excel:", err);
      toast.error("Failed to download file: " + Api.getErrorMessage(err));
    } finally {
      setIsDownloadingAppraisalPeriods(false);
    }
  };

  const handleDownloadCSV = (dataToExport?: any[]) => {
    try {
      const data = dataToExport || candidatesList;

      if (data.length === 0) {
        toast.warning("No candidate data available to export");
        return;
      }

      const csvHeaders = [
        "Extended Appraisee ID",
        "Appraisee Name",
        "Appraisal Document Name",
        "Period",
        "To",
        "Appraisal Status",
        "Rating",
      ];

      const csvRows = data.map((item: any) => {
        return [
          item.empNo || "",
          item.name || "",
          "DMRC PROBATION CONFIRMATION",
          confirmationDueOn || initiatedCycle?.confirmationDueOn || initiatedCycle?.probationDueDate || "",
          confirmationDueOn || initiatedCycle?.confirmationDueOn || initiatedCycle?.probationDueDate || "",
          "",
          "",
        ];
      });

      const escapeCsvCell = (value: any) =>
        `"${String(value ?? "").replace(/"/g, '""')}"`;
      const csvContent = [
        csvHeaders.map(escapeCsvCell).join(","),
        ...csvRows.map((row: any[]) => row.map(escapeCsvCell).join(",")),
      ].join("\r\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `probation_candidates_cycle_${cycleId}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("CSV file downloaded successfully!");
    } catch (e) {
      console.error("Failed to download CSV", e);
      toast.error("Failed to download CSV");
    }
  };

  const handleDownloadCSVStep4 = () => {
    try {
      const data = wrClearedList;

      if (data.length === 0) {
        toast.warning("No candidate data available to export");
        return;
      }

      const csvHeaders = [
        "Sr. No.",
        "Emp No",
        "Name",
        "Designation",
        "Date of Joining",
        "Confirmation Date",
        "Adjusted Confirmation Date",
        "LWP Days",
        "Suspension Days",
        "EOL Days",
        "Dies Non Days",
        "PVR Status",
        "WR Final Status",
        "DAR Status",
        "Vigilance Status",
      ];

      const csvRows = data.map((item: any, index: number) => {
        return [
          index + 1,
          item.empNo || "",
          item.name || "",
          item.designation || "",
          formatDate(item.doj),
          formatDate(item.confirmationDate),
          formatDate(item.adjustedConfirmationDate),
          item.lwpDays ?? 0,
          item.suspensionDays ?? 0,
          item.eolDays ?? 0,
          item.diesNonDays ?? 0,
          item.pvr || "CLEAR",
          item.wrFinal || "SUITABLE",
          item.dar || "PENDING",
          item.vigilance || "PENDING",
        ];
      });

      const csvContent = [
        csvHeaders.join(","),
        ...csvRows.map((row: any[]) =>
          row.map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `wr_cleared_candidates_cycle_${cycleId}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("CSV file downloaded successfully!");
    } catch (e) {
      console.error("Failed to download CSV", e);
      toast.error("Failed to download CSV");
    }
  };

  const handleSetDarStatus = async () => {
    if (!cycleId) {
      toast.error("Cycle ID is missing.");
      return;
    }
    setIsSettingDar(true);
    try {
      const response = await Api.post(
        API_URLS.PROBATION.SET_DAR_STATUS,
        { cycleId: Number(cycleId) },
        true
      );
      setClearanceResult(response?.data || response);
      toast.success("DAR clearance completed successfully!");
      updateStep(6);
    } catch (e: any) {
      console.error("DAR clearance failed", e);
      toast.error("DAR clearance failed: " + Api.getErrorMessage(e));
    } finally {
      setIsSettingDar(false);
    }
  };

  const handleSetVigilanceStatus = async () => {
    if (!cycleId) {
      toast.error("Cycle ID is missing.");
      return;
    }
    setIsSettingVigilance(true);
    try {
      const response = await Api.post(
        API_URLS.PROBATION.SET_VIGILANCE_STATUS,
        { cycleId: Number(cycleId) },
        true
      );
      setClearanceResult(response?.data || response);
      toast.success("Vigilance clearance completed successfully!");
      updateStep(7);
    } catch (e: any) {
      console.error("Vigilance clearance failed", e);
      toast.error("Vigilance clearance failed: " + Api.getErrorMessage(e));
    } finally {
      setIsSettingVigilance(false);
    }
  };

  const handleFinish = async () => {
    if (!cycleId) {
      toast.error("Cycle ID is missing.");
      return;
    }
    setIsFinishing(true);
    try {
      await Api.post(
        API_URLS.PROBATION.SET_VIGILANCE_CLEARED_TO_ELIGIBLE,
        { cycleId: Number(cycleId) },
        true
      );
      toast.success("Probation cycle setup finalized.");
      navigate(all_routes.probation.dashboard);
    } catch (e: any) {
      console.error("Failed to finalize probation cycle", e);
      toast.error("Failed to finalize probation cycle: " + Api.getErrorMessage(e));
    } finally {
      setIsFinishing(false);
    }
  };

  const columns = React.useMemo(() => [
    {
      title: "Sr. No.",
      dataIndex: "srNo",
      render: (_: any, __: any, idx: number) => ((pagination.current - 1) * pagination.pageSize) + idx + 1,
      width: 70
    },
    {
      title: "Emp No",
      dataIndex: "empNo",
      render: (val: string) => <span className="fw-semibold text-dark">{val}</span>
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (val: string) => <div className="fw-bold text-dark">{val}</div>
    },
    {
      title: "Date of Joining",
      dataIndex: "doj",
      render: (val: string) => formatDate(val)
    },
    {
      title: "Probation Period",
      dataIndex: "probationYears",
      render: (val: any) => `${val} Year(s)`
    },
    {
      title: "Confirmation Date",
      dataIndex: "confirmationDate",
      render: (val: string) => formatDate(val)
    },
    {
      title: "PVR Status",
      dataIndex: "pvrStatus",
      align: "center" as const,
      render: (val: string, record: any) => {
        const { pvrStatus } = mapPvrRecord(record);
        const status = pvrStatus || normalizePvrStatus(val);
        const theme = getPvrBadgeTheme(status);
        return (
          <span
            className="badge d-inline-flex align-items-center gap-2 fw-semibold"
            style={{
              color: theme.color,
              backgroundColor: theme.background,
              border: theme.border,
              borderRadius: "8px",
              padding: "7px 10px",
              whiteSpace: "nowrap",
            }}
          >
            <span className="rounded-circle" style={{ width: "7px", height: "7px", backgroundColor: theme.color }} />
            {status}
          </span>
        );
      }
    },
    {
      title: "PVR Remarks",
      dataIndex: "pvrRemarks",
      render: (val: string, record: any) => {
        const { pvrRemarks } = mapPvrRecord(record);
        const text = pvrRemarks !== "—" ? pvrRemarks : (val && String(val).trim() !== "—" ? String(val).trim() : "—");
        return (
          <span className="text-secondary" style={{ wordBreak: "break-word", whiteSpace: "normal", display: "inline-block", maxWidth: "300px" }}>
            {text}
          </span>
        );
      }
    },
  ], [pagination]);
  const columnsPvr = React.useMemo(() => [
    {
      title: "Sr. No.",
      dataIndex: "srNo",
      render: (_: any, __: any, idx: number) => ((pagination.current - 1) * pagination.pageSize) + idx + 1,
      width: 70
    },
    {
      title: "Emp No",
      dataIndex: "empNo",
      render: (val: string) => <span className="fw-semibold text-dark">{val}</span>
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (val: string) => <div className="fw-bold text-dark">{val}</div>
    },
    {
      title: "Date of Joining",
      dataIndex: "doj",
      render: (val: string) => formatDate(val)
    },
    {
      title: "Probation Period",
      dataIndex: "probationYears",
      render: (val: any) => `${val} Year(s)`
    },
    {
      title: "Confirmation Date",
      dataIndex: "confirmationDate",
      render: (val: string) => formatDate(val)
    },
    {
      title: "PVR Status",
      dataIndex: "pvr",
      width: 170,
      align: "center" as const,
      render: (val: string, record: any) => {
        const { pvrStatus } = mapPvrRecord(record);
        const status = pvrStatus || normalizePvrStatus(val);
        const theme = getPvrBadgeTheme(status);
        return (
          <span
            className="badge d-inline-flex align-items-center gap-2 fw-semibold"
            style={{
              color: theme.color,
              backgroundColor: theme.background,
              border: theme.border,
              borderRadius: "8px",
              padding: "7px 10px",
              whiteSpace: "nowrap",
            }}
          >
            <span className="rounded-circle" style={{ width: "7px", height: "7px", backgroundColor: theme.color }} />
            {status}
          </span>
        );
      }
    },
    {
      title: "Remarks",
      dataIndex: "pvrRemarks",
      render: (val: string, record: any) => {
        const { pvrRemarks } = mapPvrRecord(record);
        const text = pvrRemarks !== "—" ? pvrRemarks : (val && String(val).trim() !== "—" ? String(val).trim() : "—");
        return (
          <span className="text-secondary" style={{ wordBreak: "break-word", whiteSpace: "normal", display: "inline-block", maxWidth: "300px" }}>
            {text}
          </span>
        );
      }
    }
  ], [pagination]);

  const columnsStep4 = React.useMemo(() => [
    {
      title: "Sr. No.",
      dataIndex: "serialNo",
      key: "serialNo",
      render: (_: any, __: any, idx: number) => ((pagination.current - 1) * pagination.pageSize) + idx + 1,
      width: 70
    },
    {
      title: "Emp No",
      dataIndex: "empId",
      key: "empId",
      render: (val: any, record: any) => {
        const text = String(record.empId ?? record.empNo ?? val ?? "—");
        return <span className="fw-semibold text-dark">{text}</span>;
      }
    },
    {
      title: "Name",
      dataIndex: "empName",
      key: "empName",
      render: (val: any, record: any) => {
        const text = record.empName ?? record.name ?? val ?? "—";
        return <div className="fw-bold text-dark">{text}</div>;
      }
    },
    {
      title: "Date of Joining",
      dataIndex: "dateOfJoining",
      key: "dateOfJoining",
      render: (val: any, record: any) => formatDateDMY(record.dateOfJoining ?? record.doj ?? val)
    },
    {
      title: "Confirmation Date",
      dataIndex: "confirmationDate",
      key: "confirmationDate",
      render: (val: any, record: any) => formatDateDMY(record.confirmationDate ?? val)
    },
    {
      title: "Adjusted Confirmation Date",
      dataIndex: "adjustedConfirmationDate",
      key: "adjustedConfirmationDate",
      render: (val: any, record: any) => {
        const formattedAdjDate = formatDateDMY(
          record.adjustedConfirmationDate ?? record.adjusted_confirmation_date ?? record.confirmationDate
        );
        const lwp = record.lwp ?? record.adjustmentDaysLwp ?? record.adjustment_days_lwp ?? record.adjustedLwpDays ?? record.adjusted_lwp_days ?? record.lwpDays ?? record.lwp_days ?? 0;
        const suspension = record.suspension ?? record.adjustmentDaysSuspension ?? record.adjustment_days_suspension ?? record.adjustedSuspensionDays ?? record.suspensionDays ?? record.suspension_days ?? 0;
        const eol = record.eol ?? record.adjustmentDaysEol ?? record.adjustment_days_eol ?? record.adjustedEolDays ?? record.eolDays ?? record.eol_days ?? 0;
        const diesNon = record.diesNon ?? record.adjustmentDaysDiesNon ?? record.adjustment_days_dies_non ?? record.adjustedDiesNonDays ?? record.diesNonDays ?? record.dies_non_days ?? 0;

        return (
          <div className="text-nowrap">
            <div className="fw-semibold text-dark mb-1">{formattedAdjDate}</div>
            <div className="small text-muted" style={{ fontSize: "11px" }}>LWP: {lwp} Days</div>
            <div className="small text-muted" style={{ fontSize: "11px" }}>Deemed Suspension: {suspension} Days</div>
            <div className="small text-muted" style={{ fontSize: "11px" }}>EOL: {eol} Days</div>
            <div className="small text-muted" style={{ fontSize: "11px" }}>Dies Non: {diesNon} Days</div>
          </div>
        );
      }
    },
    {
      title: "PVR Status",
      dataIndex: "pvrStatus",
      key: "pvrStatus",
      align: "center" as const,
      render: (val: any, record: any) => {
        const { pvrStatus } = mapPvrRecord(record);
        const status = pvrStatus || normalizePvrStatus(val);
        const theme = getPvrBadgeTheme(status);
        return (
          <span
            className="badge d-inline-flex align-items-center gap-2 fw-semibold"
            style={{
              color: theme.color,
              backgroundColor: theme.background,
              border: theme.border,
              borderRadius: "8px",
              padding: "7px 10px",
              whiteSpace: "nowrap",
            }}
          >
            <span className="rounded-circle" style={{ width: "7px", height: "7px", backgroundColor: theme.color }} />
            {status}
          </span>
        );
      }
    },
    {
      title: "WR Final Status",
      dataIndex: "wrFinalStatus",
      key: "wrFinalStatus",
      align: "center" as const,
      render: (val: any, record: any) => {
        const rawWr = String(record.wrFinalStatus ?? record.wrFinal ?? val ?? "").trim().toUpperCase().replace(/_/g, " ");
        if (rawWr === "SUITABLE") {
          return (
            <span
              className="badge d-inline-flex align-items-center gap-2 fw-semibold"
              style={{
                color: "#15803d",
                backgroundColor: "#f0fdf4",
                border: "1px solid #86efac",
                borderRadius: "8px",
                padding: "7px 10px",
                whiteSpace: "nowrap",
              }}
            >
              <span className="rounded-circle" style={{ width: "7px", height: "7px", backgroundColor: "#15803d" }} />
              Suitable
            </span>
          );
        }
        if (rawWr === "NOT SUITABLE") {
          return (
            <span
              className="badge d-inline-flex align-items-center gap-2 fw-semibold"
              style={{
                color: "#b91c1c",
                backgroundColor: "#fef2f2",
                border: "1px solid #fca5a5",
                borderRadius: "8px",
                padding: "7px 10px",
                whiteSpace: "nowrap",
              }}
            >
              <span className="rounded-circle" style={{ width: "7px", height: "7px", backgroundColor: "#b91c1c" }} />
              Not Suitable
            </span>
          );
        }
        return (
          <span
            className="badge d-inline-flex align-items-center gap-2 fw-semibold"
            style={{
              color: "#a16207",
              backgroundColor: "#fefce8",
              border: "1px solid #fde047",
              borderRadius: "8px",
              padding: "7px 10px",
              whiteSpace: "nowrap",
            }}
          >
            <span className="rounded-circle" style={{ width: "7px", height: "7px", backgroundColor: "#a16207" }} />
            Pending
          </span>
        );
      }
    }
  ], [pagination]);

  const columnsStep5 = React.useMemo(() => [
    ...columnsStep4,
    {
      title: "DAR Status",
      dataIndex: "dar",
      key: "dar",
      align: "center" as const,
      render: (_: any, record: any) => {
        const dar = getDarDetails(record);
        return (
          <div className="d-flex align-items-center justify-content-center gap-2 text-nowrap">
            <span className={`badge ${dar.badgeClass}`} style={dar.badgeStyle}>
              {dar.statusText}
            </span>
            <button
              type="button"
              className="btn btn-xs btn-outline-primary d-inline-flex align-items-center gap-1 py-1 px-2.5"
              style={{ fontSize: "11px", borderRadius: "5px" }}
              onClick={() => setSelectedDarCandidate(record)}
            >
              <i className="ti ti-eye fs-6"></i> View Details
            </button>
          </div>
        );
      }
    }
  ], [columnsStep4]);

  const columnsVigilance = React.useMemo(() => [
    {
      title: "Sr. No.",
      dataIndex: "srNo",
      render: (_: any, __: any, idx: number) => ((pagination.current - 1) * pagination.pageSize) + idx + 1,
      width: 70
    },
    {
      title: "Emp No",
      dataIndex: "empNo",
      render: (val: string) => <span className="fw-semibold text-dark">{val}</span>
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (val: string) => <div className="fw-bold text-dark">{val}</div>
    },
    {
      title: "Date of Joining",
      dataIndex: "doj",
      render: (val: string) => formatDate(val)
    },
    {
      title: "PVR Status",
      dataIndex: "pvr",
      align: "center",
      render: (val: string) => {
        const status = normalizePvrStatus(val);
        const theme = getPvrStatusTheme(status);
        return (
          <span
            className="badge d-inline-flex align-items-center gap-2 fw-semibold"
            style={{
              color: theme.color,
              backgroundColor: theme.background,
              border: `1px solid ${theme.border}`,
              borderRadius: "8px",
              padding: "7px 10px",
              whiteSpace: "nowrap",
            }}
          >
            <span className="rounded-circle" style={{ width: "7px", height: "7px", backgroundColor: theme.color }} />
            {status}
          </span>
        );
      }
    },
    {
      title: "PVR Remarks",
      dataIndex: "pvrRemarks",
      render: (val: string, record: any) => (
        <span className="text-secondary">{record.pvrRemarks || val || "-"}</span>
      )
    },
    {
      title: "WR Final Status",
      dataIndex: "wrFinal",
      align: "center",
      render: (val: string) => {
        const finalStatus = String(val || "").trim().toUpperCase().replace(/\s+/g, "_");
        if (finalStatus === "NOT_SUITABLE") {
          return <span className="badge bg-danger-light text-danger"><i className="ti ti-x me-1"></i>NOT SUITABLE</span>;
        }
        return <span className="badge bg-success-light text-success"><i className="ti ti-check me-1"></i>SUITABLE</span>;
      }
    },
    {
      title: "DAR Status",
      dataIndex: "dar",
      align: "center",
      render: (_: any, record: any) => {
        const dar = getDarDetails(record);
        return (
          <div className="d-flex align-items-center justify-content-center gap-2 text-nowrap">
            <span className={`badge ${dar.badgeClass}`} style={dar.badgeStyle}>
              {dar.statusText}
            </span>
            <button
              type="button"
              className="btn btn-xs btn-outline-primary d-inline-flex align-items-center gap-1 py-1 px-2.5"
              style={{ fontSize: "11px", borderRadius: "5px" }}
              onClick={() => setSelectedDarCandidate(record)}
            >
              <i className="ti ti-eye fs-6"></i> View Details
            </button>
          </div>
        );
      }
    },
    {
      title: "Vigilance Status",
      dataIndex: "vigilance",
      width: 190,
      render: (val: string, record: any) => {
        const selectedStatus = normalizeVigilanceStatus(val);
        const theme = getVigilanceStatusTheme(selectedStatus);
        return (
          <div className="position-relative" style={{ minWidth: "150px" }}>
            <span
              className="position-absolute top-50 translate-middle-y rounded-circle"
              style={{ left: "13px", width: "8px", height: "8px", backgroundColor: theme.color, zIndex: 2, pointerEvents: "none" }}
            />
            <select
              className="form-select fw-semibold"
              value={selectedStatus}
              onChange={(e) => {
                const status = e.target.value;
                if (status === "Clear") {
                  setVigRemarksErrors((prev) => {
                    const next = { ...prev };
                    delete next[record.key];
                    return next;
                  });
                }
                setWrClearedList((current) =>
                  current.map((candidate: any) =>
                    candidate.key === record.key
                      ? {
                        ...candidate,
                        vigilance: status,
                        vigilanceStatus: status,
                      }
                      : candidate
                  )
                );
              }}
              style={{
                minWidth: "150px",
                height: "38px",
                paddingLeft: "30px",
                paddingRight: "32px",
                fontSize: "12px",
                color: theme.color,
                backgroundColor: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: "8px",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
                cursor: "pointer",
              }}
              aria-label={`Vigilance status for ${record.name}`}
            >
              {VIGILANCE_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status} style={{ color: "#212529", backgroundColor: "#fff" }}>{status}</option>
              ))}
            </select>
          </div>
        );
      }
    },
    {
      title: (
        <span>
          Vigilance Remarks <span className="text-muted fw-normal" style={{ fontSize: "11px" }}>(Required if Initiated/Penalty)</span>
        </span>
      ),
      dataIndex: "vigilanceRemarks",
      width: 250,
      render: (val: string, record: any) => {
        const statusNorm = normalizeVigilanceStatus(record.vigilance);
        const isMandatory = statusNorm === "Vigilance Initiated" || statusNorm === "Penalty Awarded";
        const hasError = isMandatory && !!vigRemarksErrors[record.key];
        const vigVal = record.vigilanceRemarks || "";
        return (
          <div className="position-relative">
            <input
              type="text"
              className={`form-control form-control-sm ${hasError ? "is-invalid" : ""}`}
              placeholder={isMandatory ? `Enter remarks (required for ${statusNorm})...` : "Remarks (optional)..."}
              value={vigVal}
              onChange={(e) => {
                const text = e.target.value;
                if (hasError && text.trim()) {
                  setVigRemarksErrors((prev) => {
                    const next = { ...prev };
                    delete next[record.key];
                    return next;
                  });
                }
                setWrClearedList((current) =>
                  current.map((candidate: any) =>
                    candidate.key === record.key
                      ? { ...candidate, vigilanceRemarks: text }
                      : candidate
                  )
                );
              }}
              style={{
                height: "38px",
                fontSize: "12px",
                borderRadius: "8px",
                borderColor: hasError ? "#ef4444" : "#cbd5e1",
                backgroundColor: hasError ? "#fef2f2" : "#ffffff",
              }}
            />
            {hasError && (
              <small className="text-danger d-block mt-0.5" style={{ fontSize: "11px" }}>
                Remarks required for {statusNorm}
              </small>
            )}
          </div>
        );
      }
    }
  ], [pagination, vigRemarksErrors]);
  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Initiate Probation Cycle</h3>
            <p className="text-muted small mb-0">Follow the step-by-step wizard to setup, calculate, and clear candidates</p>
          </div>
          <div>
            <Link to={all_routes.probation.dashboard} className="btn btn-light">
              Back to Dashboard
            </Link>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            {/* Step Bubbles Header */}
            <div className="mb-5 position-relative">
              <div
                className="position-absolute start-0 end-0 top-50 translate-middle-y"
                style={{ height: "3px", backgroundColor: "#e9ecef", zIndex: 1, top: "18px" }}
              ></div>
              <div className="d-flex justify-content-between align-items-center position-relative" style={{ zIndex: 2 }}>
                <StepBubble index={1} current={step} label="Cycle Details" onClick={() => updateStep(1)} />
                <StepBubble index={2} current={step} label="PVR Clearance" onClick={() => cycleId && updateStep(2)} />
                <StepBubble index={3} current={step} label="Candidacy Calculation" onClick={() => cycleId && updateStep(3)} />
                <StepBubble index={4} current={step} label="WR Grading" onClick={() => cycleId && updateStep(4)} />
                <StepBubble index={5} current={step} label="DAR Clearance" onClick={() => cycleId && updateStep(5)} />
                <StepBubble index={6} current={step} label="Vigilance Clearance" onClick={() => cycleId && updateStep(6)} />
                <StepBubble index={7} current={step} label="Summary & Finalize" onClick={() => cycleId && updateStep(7)} />
              </div>
            </div>

            {/* STEP 1: Details */}
            {step === 1 && (
              <div className="animate-fade-in">
                <h5 className="mb-1 fw-bold text-dark">Step 1/7: Cycle Details</h5>
                <p className="text-muted small mb-4">Define target financial year, quarter, grade, base date, and descriptive title for the cycle.</p>

                <div className="row g-4">
                  {/* Financial Year */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">Financial Year <span className="text-danger">*</span></label>
                    <select
                      className="form-select py-2.5 border-grey"
                      value={financialYear}
                      onChange={(e) => setFinancialYear(e.target.value)}
                      disabled
                    >
                      <option value={`${currentYear - 2}-${currentYear - 1}`}>{currentYear - 2}-{currentYear - 1}</option>
                      <option value={`${currentYear - 1}-${currentYear}`}>{currentYear - 1}-{currentYear}</option>
                      <option value={`${currentYear}-${currentYear + 1}`}>{currentYear}-{currentYear + 1}</option>
                    </select>
                  </div>

                  {/* Employee Type */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">Employee Type <span className="text-danger">*</span></label>
                    <select
                      className="form-select py-2.5 border-grey"
                      value={employeeType}
                      onChange={(e) => setEmployeeType(e.target.value)}
                    >
                      <option value="">Select employee type</option>
                      <option value="Executive">Executive</option>
                      <option value="Non Executive">Non Executive</option>
                    </select>
                  </div>

                  {/* Pay Scale multi-select */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">Pay Scale <span className="text-danger">*</span></label>
                    <div ref={gradeDropdownRef} className="position-relative">
                      {/* Trigger Button */}
                      <div
                        onClick={() => {
                          if (employeeType && !loadingGrades) {
                            setGradeDropdownOpen(o => !o);
                          }
                        }}
                        className="form-control d-flex align-items-center justify-content-between"
                        style={{
                          cursor: employeeType && !loadingGrades ? "pointer" : "not-allowed",
                          height: "auto",
                          minHeight: "42px",
                          maxHeight: "120px",
                          overflowY: "auto",
                          userSelect: "none",
                          backgroundColor: employeeType ? "#fff" : "#f8f9fa",
                          padding: "6px 12px"
                        }}
                      >
                        {grades.length === 0 ? (
                          <span className="text-muted">
                            {loadingGrades ? "Loading pay scales..." : employeeType ? "Select pay scale(s)" : "Select employee type first"}
                          </span>
                        ) : isAllGradesSelected ? (
                          <span className="badge bg-primary-transparent text-primary d-inline-flex align-items-center" style={{ fontSize: "12px", padding: "5px 10px" }}>
                            All Pay Scales Selected ({allPayScales.length})
                            <span
                              onClick={e => { e.stopPropagation(); setGrades([]); }}
                              style={{ marginLeft: "8px", cursor: "pointer", fontWeight: 700 }}
                            >×</span>
                          </span>
                        ) : (
                          <div className="d-flex flex-wrap gap-1" style={{ flex: 1, minWidth: 0 }}>
                            {grades.map(g => (
                              <span key={g} className="badge bg-primary-transparent text-primary" style={{ fontSize: "12px", padding: "4px 8px" }}>
                                {g}
                                <span
                                  onClick={e => { e.stopPropagation(); toggleGrade(g); }}
                                  style={{ marginLeft: "6px", cursor: "pointer", fontWeight: 700 }}
                                >×</span>
                              </span>
                            ))}
                          </div>
                        )}
                        <i className={`ti ti-chevron-${gradeDropdownOpen ? "up" : "down"} ms-2`} style={{ fontSize: "14px", flexShrink: 0 }} />
                      </div>

                      {/* Dropdown Panel */}
                      {gradeDropdownOpen && (
                        <div
                          className="position-absolute bg-white border rounded-3 shadow"
                          style={{ top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 1050, maxHeight: "220px", overflowY: "auto" }}
                        >
                          {gradesMaster.length === 0 ? (
                            <div className="p-3 text-muted small">No pay scales available</div>
                          ) : (
                            <>
                              <label
                                className="d-flex align-items-center gap-2 px-3 py-2 fw-semibold"
                                style={{ cursor: "pointer", margin: 0, borderBottom: "2px solid #e5e7eb", backgroundColor: "#f9fafb" }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isAllGradesSelected}
                                  onChange={toggleSelectAllGrades}
                                  style={{ width: "16px", height: "16px", accentColor: "#3b82f6", flexShrink: 0 }}
                                />
                                <span style={{ fontSize: "13px" }}>Select All</span>
                              </label>
                              {gradesMaster.map((g: any, index: number) => {
                                const payScale = getPayScaleValue(g);
                                if (!payScale) return null;
                                return (
                                  <label
                                    key={g.id || payScale || index}
                                    className="d-flex align-items-center gap-2 px-3 py-2"
                                    style={{ cursor: "pointer", margin: 0, borderBottom: "1px solid #f3f4f6" }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={grades.includes(payScale)}
                                      onChange={() => toggleGrade(payScale)}
                                      style={{ width: "16px", height: "16px", accentColor: "#3b82f6", flexShrink: 0 }}
                                    />
                                    <span style={{ fontSize: "13px" }}>{payScale}</span>
                                  </label>
                                );
                              })}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Employment Type */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">Employment Type</label>
                    <select
                      className="form-select py-2.5 border-grey"
                      value={employmentType}
                      onChange={(e) => setEmploymentType(e.target.value)}
                      disabled={loadingEmploymentTypes}
                    >
                      <option value="">
                        {loadingEmploymentTypes ? "Loading employment types..." : "Select employment type"}
                      </option>
                      {employmentTypesList.map((item: any, idx: number) => {
                        const val = getEmploymentTypeValue(item);
                        const label = getEmploymentTypeLabel(item);
                        return (
                          <option key={item?.id || val || idx} value={val}>
                            {label}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Employee Range */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">Employee ID Range Filter</label>
                    <select
                      className="form-select py-2.5 border-grey"
                      value={employeeRange}
                      onChange={(e) => setEmployeeRange(e.target.value)}
                    >
                      <option value="">All Employees</option>
                      <option value="below_5000">Below 5000 (IDs &lt; 5000)</option>
                      <option value="above_5000">Above 5000 (IDs &ge; 5000)</option>
                    </select>
                  </div>

                  {/* Confirmation Due On */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">Confirmation Due On</label>
                    <input
                      type="date"
                      className="form-control py-2.5 border-grey"
                      value={confirmationDueOn}
                      onChange={(e) => setConfirmationDueOn(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mt-5 d-flex justify-content-end">
                  <button className="btn btn-primary btn-lg px-4 py-2.5" onClick={handleInitiate} disabled={isInitiating}>
                    Initiate Cycle & Continue
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: Calculate */}
            {step === 3 && (
              <div className="animate-fade-in">
                <h5 className="mb-1 fw-bold text-dark">Step 3/7: Candidacy Calculation</h5>
                <p className="text-muted small mb-4">Review the matched candidates below. Run the background calculation to analyze joins, dates, and probation durations to determine candidates.</p>

                {/* KPI Breakdown Cards */}
                <div className="row g-3 mb-4">
                  {/* Total Candidates Card */}
                  <div className="col-md-3">
                    <div
                      className="card border-0 shadow-xs p-3 rounded-3"
                      onClick={() => {
                        setPvrStatusFilter("ALL");
                        setPagination((prev) => ({ ...prev, current: 1 }));
                      }}
                      style={{
                        backgroundColor: pvrStatusFilter === "ALL" ? "#eff6ff" : "#f8fafc",
                        borderLeft: "4px solid #3b82f6",
                        border: pvrStatusFilter === "ALL" ? "2px solid #3b82f6" : "1px solid #e2e8f0",
                        cursor: "pointer",
                        boxShadow: pvrStatusFilter === "ALL" ? "0 4px 12px rgba(59, 130, 246, 0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
                        transition: "all 0.2s ease",
                      }}
                      title="Click to view all candidates"
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="text-muted small fw-semibold">Total Candidates</span>
                        {pvrStatusFilter === "ALL" && (
                          <span className="badge bg-primary text-white rounded-pill px-2 py-0.5" style={{ fontSize: "10px" }}>
                            Active Filter
                          </span>
                        )}
                      </div>
                      <div className="fs-3 fw-bold text-dark mt-1">{candidatesList.length}</div>
                    </div>
                  </div>

                  {/* NO ADVERSE Card */}
                  <div className="col-md-3">
                    <div
                      className="card border-0 shadow-xs p-3 rounded-3"
                      onClick={() => {
                        setPvrStatusFilter((prev) => (prev === "NO ADVERSE" ? "ALL" : "NO ADVERSE"));
                        setPagination((prev) => ({ ...prev, current: 1 }));
                      }}
                      style={{
                        backgroundColor: pvrStatusFilter === "NO ADVERSE" ? "#dcfce7" : "#f0fdf4",
                        borderLeft: "4px solid #16a34a",
                        border: pvrStatusFilter === "NO ADVERSE" ? "2px solid #16a34a" : "1px solid #bbf7d0",
                        cursor: "pointer",
                        boxShadow: pvrStatusFilter === "NO ADVERSE" ? "0 4px 12px rgba(22, 163, 74, 0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
                        transition: "all 0.2s ease",
                      }}
                      title="Click to filter NO ADVERSE candidates"
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="text-success small fw-semibold">NO ADVERSE</span>
                        {pvrStatusFilter === "NO ADVERSE" && (
                          <span className="badge bg-success text-white rounded-pill px-2 py-0.5" style={{ fontSize: "10px" }}>
                            Active Filter
                          </span>
                        )}
                      </div>
                      <div className="fs-3 fw-bold text-success mt-1">
                        {candidatesList.filter((c: any) => mapPvrRecord(c).pvrStatus === "NO ADVERSE").length}
                      </div>
                    </div>
                  </div>

                  {/* ISSUE IN PVR Card */}
                  <div className="col-md-3">
                    <div
                      className="card border-0 shadow-xs p-3 rounded-3"
                      onClick={() => {
                        setPvrStatusFilter((prev) => (prev === "ISSUE IN PVR" ? "ALL" : "ISSUE IN PVR"));
                        setPagination((prev) => ({ ...prev, current: 1 }));
                      }}
                      style={{
                        backgroundColor: pvrStatusFilter === "ISSUE IN PVR" ? "#fee2e2" : "#fef2f2",
                        borderLeft: "4px solid #dc2626",
                        border: pvrStatusFilter === "ISSUE IN PVR" ? "2px solid #dc2626" : "1px solid #fca5a5",
                        cursor: "pointer",
                        boxShadow: pvrStatusFilter === "ISSUE IN PVR" ? "0 4px 12px rgba(220, 38, 38, 0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
                        transition: "all 0.2s ease",
                      }}
                      title="Click to filter ISSUE IN PVR candidates"
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="text-danger small fw-semibold">ISSUE IN PVR</span>
                        {pvrStatusFilter === "ISSUE IN PVR" && (
                          <span className="badge bg-danger text-white rounded-pill px-2 py-0.5" style={{ fontSize: "10px" }}>
                            Active Filter
                          </span>
                        )}
                      </div>
                      <div className="fs-3 fw-bold text-danger mt-1">
                        {candidatesList.filter((c: any) => mapPvrRecord(c).pvrStatus === "ISSUE IN PVR").length}
                      </div>
                    </div>
                  </div>

                  {/* In Process Card */}
                  <div className="col-md-3">
                    <div
                      className="card border-0 shadow-xs p-3 rounded-3"
                      onClick={() => {
                        setPvrStatusFilter((prev) => (prev === "In Process" ? "ALL" : "In Process"));
                        setPagination((prev) => ({ ...prev, current: 1 }));
                      }}
                      style={{
                        backgroundColor: pvrStatusFilter === "In Process" ? "#fef3c7" : "#fffbeb",
                        borderLeft: "4px solid #d97706",
                        border: pvrStatusFilter === "In Process" ? "2px solid #d97706" : "1px solid #fde68a",
                        cursor: "pointer",
                        boxShadow: pvrStatusFilter === "In Process" ? "0 4px 12px rgba(217, 119, 6, 0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
                        transition: "all 0.2s ease",
                      }}
                      title="Click to filter In Process candidates"
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <span className="text-warning small fw-semibold">In Process</span>
                        {pvrStatusFilter === "In Process" && (
                          <span className="badge bg-warning text-dark rounded-pill px-2 py-0.5" style={{ fontSize: "10px" }}>
                            Active Filter
                          </span>
                        )}
                      </div>
                      <div className="fs-3 fw-bold text-warning mt-1">
                        {candidatesList.filter((c: any) => mapPvrRecord(c).pvrStatus === "In Process").length}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="alert alert-info border-0 p-3 mb-4 rounded-3 d-flex align-items-center" style={{ backgroundColor: "rgba(14, 116, 144, 0.05)", color: "#0e7490" }}>
                  <i className="ti ti-info-circle fs-4 me-3"></i>
                  <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
                    <div>
                      <strong>Cycle Initiated!</strong> ID: #{cycleId}. Matched <strong>{candidatesList.length}</strong> candidate(s) in database.
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        className="btn btn-sm btn-warning fw-semibold"
                        onClick={handleCalculate}
                        disabled={isCalculating}
                      >
                        Run Calculations Now
                      </button>
                    </div>
                  </div>
                </div>

                <div className="card border shadow-xs mb-4">
                  <div className="card-body p-0">
                    <Table
                      columns={columns}
                      dataSource={filteredCandidatesList}
                      paginationConfig={pagination}
                      Selection={false}
                      loading={loadingCandidates}
                      scroll={{ x: "max-content" }}
                      handlePagination={(pag: any) => {
                        setPagination({
                          current: pag.current,
                          pageSize: pag.pageSize
                        });
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap p-2.5 border-bottom bg-light">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted small text-nowrap fw-medium" style={{ fontSize: "12px" }}>
                            <i className="ti ti-filter me-1 text-primary"></i>Showing:
                          </span>
                          <span className="badge bg-white text-dark border px-2.5 py-1.5 rounded-pill fw-semibold" style={{ fontSize: "12px" }}>
                            {pvrStatusFilter === "ALL" ? `All Candidates (${candidatesList.length})` : `${pvrStatusFilter} (${filteredCandidatesList.length})`}
                          </span>
                          {pvrStatusFilter !== "ALL" && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-danger p-0 text-decoration-none small fw-semibold"
                              onClick={() => {
                                setPvrStatusFilter("ALL");
                                setPagination((prev) => ({ ...prev, current: 1 }));
                              }}
                              style={{ fontSize: "12px" }}
                            >
                              <i className="ti ti-rotate-clockwise"></i> Reset Filter
                            </button>
                          )}
                        </div>

                        {/* Search Input */}
                        <div className="position-relative" style={{ minWidth: "200px", maxWidth: "260px" }}>
                          <input
                            type="search"
                            className="form-control form-control-sm"
                            placeholder="Search candidate ID or name..."
                            value={pvrSearchTerm}
                            onChange={(e) => {
                              setPvrSearchTerm(e.target.value);
                              setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            style={{ height: "32px", fontSize: "12px", borderRadius: "6px" }}
                          />
                        </div>
                      </div>
                    </Table>
                  </div>
                </div>

                <div className="mt-5 d-flex justify-content-between">
                  <button className="btn btn-light border px-4" onClick={goPrev} disabled={isCalculating}>
                    Back
                  </button>
                </div>
              </div>
            )}

            {/* STEP 2: PVR Clearance */}
            {step === 2 && (
              <div className="animate-fade-in">
                {/* Header Row */}
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <h5 className="mb-0 fw-bold text-dark">Step 2/7: PVR Clearance</h5>
                      <span className="badge bg-soft-primary text-primary px-2.5 py-1 rounded-pill fw-semibold" style={{ fontSize: "11px" }}>
                        {pvrClearedList.length} Candidate{pvrClearedList.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <p className="text-muted small mb-0 mt-1">Review employee police verification status and remarks.</p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button
                      className="btn btn-outline-primary px-3 py-2 fw-semibold d-inline-flex align-items-center gap-2 shadow-xs bg-white"
                      onClick={handleRefreshPvrStatus}
                      disabled={isRefreshingPvr || pvrClearedList.length === 0}
                      style={{ borderRadius: "8px", height: "40px" }}
                    >
                      {isRefreshingPvr ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          Refreshing PVR statuses…
                        </>
                      ) : (
                        <>
                          <i className="ti ti-refresh" style={{ fontSize: "16px" }}></i>
                          Refresh PVR Status
                        </>
                      )}
                    </button>
                    <button
                      className="btn btn-primary px-4 py-2 fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
                      onClick={() => updateStep(3)}
                      disabled={pvrClearedList.length === 0}
                      style={{ borderRadius: "8px", height: "40px" }}
                    >
                      <span>Continue</span>
                      <i className="ti ti-arrow-right" style={{ fontSize: "16px" }}></i>
                    </button>
                  </div>
                </div>

                {/* Clean Filter Chips Bar */}
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 p-3 bg-white border rounded-3 shadow-xs">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="text-muted small fw-semibold me-1">
                      <i className="ti ti-filter text-primary me-1"></i>Filter Status:
                    </span>
                    {/* All Pill */}
                    <button
                      type="button"
                      onClick={() => setPvrStatusFilter("ALL")}
                      className="btn btn-sm p-0 border-0 shadow-none"
                      style={{ background: "transparent" }}
                    >
                      <span
                        className="d-inline-flex align-items-center gap-2 fw-semibold transition-all"
                        style={{
                          color: pvrStatusFilter === "ALL" ? "#0f172a" : "#64748b",
                          backgroundColor: pvrStatusFilter === "ALL" ? "#f1f5f9" : "#ffffff",
                          border: pvrStatusFilter === "ALL" ? "1.5px solid #0f172a" : "1px solid #e2e8f0",
                          borderRadius: "20px",
                          padding: "5px 12px",
                          fontSize: "12px",
                          cursor: "pointer",
                        }}
                      >
                        <span>All</span>
                        <span
                          className="rounded-pill fw-bold d-inline-flex align-items-center justify-content-center"
                          style={{
                            backgroundColor: pvrStatusFilter === "ALL" ? "#0f172a" : "#f1f5f9",
                            color: pvrStatusFilter === "ALL" ? "#ffffff" : "#475569",
                            fontSize: "11px",
                            minWidth: "20px",
                            height: "18px",
                            padding: "0 6px"
                          }}
                        >
                          {pvrClearedList.length}
                        </span>
                      </span>
                    </button>

                    {PVR_STATUS_OPTIONS.map((status) => {
                      const count = pvrStatusCounts[status] || 0;
                      const theme = getPvrBadgeTheme(status);
                      const isSelected = pvrStatusFilter === status;
                      return (
                        <button
                          key={status}
                          type="button"
                          onClick={() => setPvrStatusFilter(isSelected ? "ALL" : status)}
                          className="btn btn-sm p-0 border-0 shadow-none"
                          style={{ background: "transparent" }}
                        >
                          <span
                            className="d-inline-flex align-items-center gap-2 fw-semibold transition-all"
                            style={{
                              color: isSelected ? theme.color : count > 0 ? "#334155" : "#94a3b8",
                              backgroundColor: isSelected ? theme.background : "#ffffff",
                              border: isSelected ? `1.5px solid ${theme.color}` : "1px solid #e2e8f0",
                              borderRadius: "20px",
                              padding: "5px 12px",
                              fontSize: "12px",
                              cursor: "pointer",
                            }}
                          >
                            <span
                              className="rounded-circle flex-shrink-0"
                              style={{
                                width: "8px",
                                height: "8px",
                                backgroundColor: theme.color,
                                opacity: count > 0 || isSelected ? 1 : 0.4
                              }}
                            />
                            <span>{status}</span>
                            <span
                              className="rounded-pill fw-bold d-inline-flex align-items-center justify-content-center"
                              style={{
                                backgroundColor: isSelected ? theme.color : "#f1f5f9",
                                color: isSelected ? "#ffffff" : count > 0 ? "#334155" : "#94a3b8",
                                fontSize: "11px",
                                minWidth: "20px",
                                height: "18px",
                                padding: "0 6px"
                              }}
                            >
                              {count}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  {pvrStatusFilter !== "ALL" && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0 text-decoration-none small fw-semibold d-inline-flex align-items-center gap-1"
                      onClick={() => setPvrStatusFilter("ALL")}
                      style={{ fontSize: "12px" }}
                    >
                      <i className="ti ti-rotate-clockwise"></i> Reset Filter
                    </button>
                  )}
                </div>

                {/* Table Container */}
                <div className="card border shadow-xs mb-4">
                  <div className="card-body p-0">
                    <Table
                      columns={columnsPvr}
                      dataSource={filteredPvrList}
                      paginationConfig={pagination}
                      Selection={false}
                      loading={loadingPvrCleared || isRefreshingPvr}
                      scroll={{ x: "max-content" }}
                      handlePagination={(pag: any) => {
                        setPagination({
                          current: pag.current,
                          pageSize: pag.pageSize
                        });
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap p-2.5 border-bottom bg-light">
                        {/* PVR Status Filter Dropdown */}
                        <div className="d-flex align-items-center gap-1.5">
                          <span className="text-muted small text-nowrap fw-medium" style={{ fontSize: "12px" }}>
                            <i className="ti ti-filter me-1 text-primary"></i>Status Filter:
                          </span>
                          <select
                            className="form-select form-select-sm"
                            value={pvrStatusFilter}
                            onChange={(e) => setPvrStatusFilter(e.target.value)}
                            style={{
                              width: "160px",
                              height: "32px",
                              fontSize: "12px",
                              borderRadius: "6px",
                              borderColor: pvrStatusFilter !== "ALL" ? "#3b82f6" : "#dee2e6",
                              backgroundColor: pvrStatusFilter !== "ALL" ? "#eff6ff" : "#fff",
                              fontWeight: pvrStatusFilter !== "ALL" ? 600 : 400
                            }}
                          >
                            <option value="ALL">All Statuses ({pvrClearedList.length})</option>
                            {PVR_STATUS_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt} ({pvrStatusCounts[opt] || 0})
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Search Input */}
                        <div className="position-relative" style={{ minWidth: "180px", maxWidth: "240px" }}>
                          <input
                            type="search"
                            className="form-control form-control-sm"
                            placeholder="Search candidates..."
                            value={pvrSearchTerm}
                            onChange={(e) => {
                              setPvrSearchTerm(e.target.value);
                              setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            style={{ height: "32px", fontSize: "12px", borderRadius: "6px" }}
                          />
                        </div>

                        {/* Export to Excel Button */}
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1.5 bg-white shadow-xs fw-semibold"
                          onClick={handleExportPvrExcel}
                          disabled={pvrClearedList.length === 0}
                          style={{ height: "32px", borderRadius: "6px", fontSize: "12px" }}
                          title="Export candidate list to Excel"
                        >
                          <i className="ti ti-file-spreadsheet text-success" style={{ fontSize: "15px" }}></i>
                          <span>Export Excel</span>
                        </button>
                      </div>
                    </Table>
                  </div>
                </div>

                <div className="mt-4 d-flex justify-content-between">
                  <button className="btn btn-light border px-4" onClick={goPrev} disabled={isRefreshingPvr}>
                    Back
                  </button>
                  <button className="btn btn-primary px-4 fw-semibold" onClick={() => updateStep(3)} disabled={isRefreshingPvr}>
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: WR Grading */}
            {step === 4 && (
              <div className="animate-fade-in">
                <h5 className="mb-1 fw-bold text-dark">Step 4/7: WR Grading</h5>
                <p className="text-muted small mb-4">Download the candidate template, complete the WR grading details, and upload the CSV before proceeding.</p>

                <div className="alert alert-info border-0 p-3 mb-4 rounded-3 d-flex align-items-center" style={{ backgroundColor: "rgba(14, 116, 144, 0.05)", color: "#0e7490" }}>
                  <i className="ti ti-file-spreadsheet fs-4 me-3"></i>
                  <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
                    <div><strong>WR Grading:</strong> Prepare grading for <strong>{pvrClearedList.length}</strong> candidate(s).</div>
                    <div className="d-flex align-items-center gap-2">
                      <button className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 bg-white" onClick={handleDownloadAppraisalPeriodsExcel} disabled={isDownloadingAppraisalPeriods}>
                        <i className="ti ti-download" /> {isDownloadingAppraisalPeriods ? "Downloading..." : "Download CSV"}
                      </button>
                      <input type="file" ref={wrFileInputRef} onChange={handleUploadWr} accept=".csv, .xlsx, .xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" style={{ display: "none" }} />
                      <button className={`btn btn-sm d-flex align-items-center gap-1 ${wrUploaded ? "btn-outline-success bg-white" : "btn-outline-danger bg-white"}`} onClick={() => wrFileInputRef.current?.click()} disabled={isUploadingWr}>
                        <i className="ti ti-upload" /> {isUploadingWr ? "Uploading..." : wrUploaded ? "WR Grading Uploaded" : "Upload WR Grading CSV"}
                      </button>
                      <button className="btn btn-sm btn-success fw-semibold" onClick={() => setStep(5)} disabled={pvrClearedList.length === 0}>
                        Proceed to Clearance Auditing
                      </button>
                    </div>
                  </div>
                </div>

                <div className="card border shadow-xs mb-4">
                  <div className="card-body p-0">
                    <Table
                      columns={columnsStep4}
                      dataSource={filteredWrStep4List}
                      paginationConfig={pagination}
                      Selection={false}
                      loading={loadingPvrCleared}
                      scroll={{ x: "max-content" }}
                      handleSearch={(val: string) => {
                        setWrSearchTerm(val);
                        setPagination(prev => ({ ...prev, current: 1 }));
                      }}
                      searchValue={wrSearchTerm}
                      searchPlaceholder="Search candidates..."
                      handlePagination={(pag: any) => setPagination({ current: pag.current, pageSize: pag.pageSize })}
                    />
                  </div>
                </div>

                <div className="mt-5 d-flex justify-content-between">
                  <button className="btn btn-light border px-4" onClick={goPrev} disabled={isUploadingWr}>Back</button>
                </div>
              </div>
            )}

            {/* STEP 5: DAR Clearance */}
            {step === 5 && (
              <div className="animate-fade-in">
                <h5 className="mb-1 fw-bold text-dark">Step 5/7: DAR Clearance</h5>
                <p className="text-muted small mb-4">Review WR-suitable candidates and run the DAR clearance check.</p>

                <div className="alert alert-success border-0 p-3 mb-4 rounded-3 d-flex align-items-center" style={{ backgroundColor: "rgba(22, 163, 74, 0.05)", color: "#16a34a" }}>
                  <i className="ti ti-circle-check fs-4 me-3"></i>
                  <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
                    <div>
                      <strong>Ready for DAR Clearance:</strong> Found <strong>{wrClearedList.length}</strong> WR-suitable candidate(s).
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        className="btn btn-sm btn-success fw-semibold"
                        onClick={handleSetDarStatus}
                        disabled={isSettingDar || wrClearedList.length === 0}
                      >
                        {isSettingDar ? "Running DAR Clearance..." : "Run DAR Clearance & Continue"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="card border shadow-xs mb-4">
                  <div className="card-body p-0">
                    <Table
                      columns={columnsStep5}
                      dataSource={filteredDarList}
                      paginationConfig={pagination}
                      Selection={false}
                      loading={loadingWrCleared}
                      scroll={{ x: "max-content" }}
                      handleSearch={(val: string) => {
                        setDarSearchTerm(val);
                        setPagination(prev => ({ ...prev, current: 1 }));
                      }}
                      searchValue={darSearchTerm}
                      searchPlaceholder="Search candidates..."
                      handlePagination={(pag: any) => {
                        setPagination({
                          current: pag.current,
                          pageSize: pag.pageSize
                        });
                      }}
                    />
                  </div>
                </div>

                <div className="mt-5 d-flex justify-content-between">
                  <button className="btn btn-light border px-4" onClick={goPrev} disabled={isSettingDar}>
                    Back
                  </button>
                </div>
              </div>
            )}

            {/* STEP 6: Vigilance Clearance */}
            {step === 6 && (
              <div className="animate-fade-in">
                {/* Header Row */}
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="d-flex align-items-center gap-2">
                      <h5 className="mb-0 fw-bold text-dark">Step 6/7: Vigilance Clearance</h5>
                      <span className="badge bg-soft-primary text-primary px-2.5 py-1 rounded-pill fw-semibold" style={{ fontSize: "11px" }}>
                        {wrClearedList.length} Candidate{wrClearedList.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <p className="text-muted small mb-0 mt-1">Review and set Vigilance clearance statuses (Pending / Clear) and mandatory remarks individually or in bulk.</p>
                  </div>
                  <button
                    className="btn btn-success px-4 py-2 fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
                    onClick={handleSaveVigilanceStatuses}
                    disabled={isSettingVigilance || wrClearedList.length === 0}
                    style={{ borderRadius: "8px", height: "40px" }}
                  >
                    {isSettingVigilance ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-check" style={{ fontSize: "16px" }}></i>
                        Save Vigilance Statuses & Continue
                      </>
                    )}
                  </button>
                </div>

                {/* Unified Filter & Bulk Action Panel */}
                <div
                  className="card border-0 mb-4 shadow-sm"
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.03)"
                  }}
                >
                  <div className="card-body p-3.5">
                    {/* Top Tier: Filter by Status Pills */}
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                      <div className="d-flex align-items-center gap-1.5 text-muted small fw-semibold">
                        <i className="ti ti-filter text-primary" style={{ fontSize: "15px" }}></i>
                        <span style={{ fontSize: "12px", letterSpacing: "0.2px" }}>Filter by Status:</span>
                      </div>

                      {vigStatusFilter !== "ALL" && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm text-danger p-0 text-decoration-none small fw-semibold d-inline-flex align-items-center gap-1 ms-auto"
                          onClick={() => setVigStatusFilter("ALL")}
                          style={{ fontSize: "11px" }}
                        >
                          <i className="ti ti-rotate-clockwise"></i> Reset Filter
                        </button>
                      )}
                    </div>

                    <div className="d-flex flex-wrap align-items-center gap-2">
                      {/* All Status Pill */}
                      <button
                        type="button"
                        onClick={() => setVigStatusFilter("ALL")}
                        className="btn btn-sm p-0 border-0 shadow-none"
                        style={{ background: "transparent" }}
                      >
                        <span
                          className="d-inline-flex align-items-center gap-2 fw-semibold transition-all"
                          style={{
                            color: vigStatusFilter === "ALL" ? "#0f172a" : "#64748b",
                            backgroundColor: vigStatusFilter === "ALL" ? "#f1f5f9" : "#ffffff",
                            border: vigStatusFilter === "ALL" ? "1.5px solid #0f172a" : "1px solid #e2e8f0",
                            borderRadius: "20px",
                            padding: "5px 12px",
                            fontSize: "12px",
                            cursor: "pointer",
                            boxShadow: vigStatusFilter === "ALL" ? "0 2px 6px rgba(15, 23, 42, 0.08)" : "none",
                          }}
                        >
                          <span>All</span>
                          <span
                            className="rounded-pill fw-bold d-inline-flex align-items-center justify-content-center"
                            style={{
                              backgroundColor: vigStatusFilter === "ALL" ? "#0f172a" : "#f1f5f9",
                              color: vigStatusFilter === "ALL" ? "#ffffff" : "#475569",
                              fontSize: "11px",
                              minWidth: "20px",
                              height: "18px",
                              padding: "0 6px"
                            }}
                          >
                            {wrClearedList.length}
                          </span>
                        </span>
                      </button>

                      {/* Status Pills */}
                      {VIGILANCE_STATUS_OPTIONS.map((status) => {
                        const count = vigStatusCounts[status] || 0;
                        const theme = getVigilanceStatusTheme(status);
                        const isSelected = vigStatusFilter === status;
                        return (
                          <button
                            key={status}
                            type="button"
                            onClick={() => setVigStatusFilter(isSelected ? "ALL" : status)}
                            className="btn btn-sm p-0 border-0 shadow-none"
                            style={{ background: "transparent" }}
                          >
                            <span
                              className="d-inline-flex align-items-center gap-2 fw-semibold transition-all"
                              style={{
                                color: isSelected ? theme.color : count > 0 ? "#334155" : "#94a3b8",
                                backgroundColor: isSelected ? theme.background : "#ffffff",
                                border: isSelected ? `1.5px solid ${theme.color}` : "1px solid #e2e8f0",
                                borderRadius: "20px",
                                padding: "5px 12px",
                                fontSize: "12px",
                                cursor: "pointer",
                                boxShadow: isSelected ? `0 2px 8px ${theme.color}26` : "none",
                              }}
                            >
                              <span
                                className="rounded-circle flex-shrink-0"
                                style={{
                                  width: "8px",
                                  height: "8px",
                                  backgroundColor: theme.color,
                                  opacity: count > 0 || isSelected ? 1 : 0.4
                                }}
                              />
                              <span>{status}</span>
                              <span
                                className="rounded-pill fw-bold d-inline-flex align-items-center justify-content-center"
                                style={{
                                  backgroundColor: isSelected ? theme.color : "#f1f5f9",
                                  color: isSelected ? "#ffffff" : count > 0 ? "#334155" : "#94a3b8",
                                  fontSize: "11px",
                                  minWidth: "20px",
                                  height: "18px",
                                  padding: "0 6px"
                                }}
                              >
                                {count}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <hr className="my-3" style={{ borderColor: "#f1f5f9", opacity: 0.8 }} />

                    {/* Bottom Tier: Bulk Update Controls */}
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                      {/* Left: Bulk Selector, Remarks & Buttons */}
                      <div className="d-flex flex-wrap align-items-center gap-2.5">
                        <div className="d-flex align-items-center gap-2">
                          <span className="fw-semibold text-dark small text-nowrap d-flex align-items-center gap-1.5" style={{ fontSize: "12px" }}>
                            <i className="ti ti-layers-intersect text-primary fs-6"></i>
                            Bulk Update:
                          </span>
                          <div className="position-relative" style={{ minWidth: "140px" }}>
                            {(() => {
                              const theme = getVigilanceStatusTheme(bulkVigStatus);
                              return (
                                <>
                                  <span
                                    className="position-absolute top-50 translate-middle-y rounded-circle"
                                    style={{ left: "12px", width: "8px", height: "8px", backgroundColor: theme.color, zIndex: 2, pointerEvents: "none" }}
                                  />
                                  <select
                                    className="form-select form-select-sm fw-semibold"
                                    value={bulkVigStatus}
                                    onChange={(e) => setBulkVigStatus(e.target.value)}
                                    style={{
                                      paddingLeft: "28px",
                                      fontSize: "12px",
                                      height: "34px",
                                      borderRadius: "8px",
                                      color: theme.color,
                                      backgroundColor: theme.background,
                                      border: `1px solid ${theme.border}`,
                                    }}
                                  >
                                    {VIGILANCE_STATUS_OPTIONS.map((opt) => (
                                      <option key={opt} value={opt} style={{ color: "#212529", backgroundColor: "#fff" }}>
                                        {opt}
                                      </option>
                                    ))}
                                  </select>
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Bulk Remarks Input */}
                        <div className="position-relative" style={{ minWidth: "180px", maxWidth: "240px" }}>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Bulk remarks (optional)..."
                            value={bulkVigRemarks}
                            onChange={(e) => setBulkVigRemarks(e.target.value)}
                            style={{ height: "34px", borderRadius: "8px", fontSize: "12px" }}
                          />
                        </div>

                        <button
                          type="button"
                          className="btn btn-primary btn-sm px-3 fw-semibold d-inline-flex align-items-center gap-1.5 text-nowrap"
                          onClick={() => handleBulkApplyVigStatus("selected")}
                          disabled={selectedVigRowKeys.length === 0}
                          style={{ height: "34px", borderRadius: "8px", fontSize: "12px" }}
                        >
                          <i className="ti ti-check" style={{ fontSize: "14px" }}></i>
                          Apply to Selected ({selectedVigRowKeys.length})
                        </button>

                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm px-3 fw-semibold d-inline-flex align-items-center gap-1.5 text-nowrap bg-white"
                          onClick={() => handleBulkApplyVigStatus("all")}
                          disabled={wrClearedList.length === 0}
                          style={{ height: "34px", borderRadius: "8px", fontSize: "12px" }}
                        >
                          <i className="ti ti-list-check" style={{ fontSize: "14px" }}></i>
                          Apply to All ({wrClearedList.length})
                        </button>
                      </div>

                      {/* Right: Quick Action Shortcuts */}
                      <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small" style={{ fontSize: "12px" }}>Quick Action:</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-success bg-white fw-semibold d-inline-flex align-items-center gap-1.5 text-nowrap shadow-xs"
                          onClick={() => handleQuickSetAllVig("Clear")}
                          disabled={wrClearedList.length === 0}
                          style={{ height: "34px", borderRadius: "8px", fontSize: "12px" }}
                          title="Mark all candidates as Clear"
                        >
                          <i className="ti ti-circle-check text-success" style={{ fontSize: "15px" }}></i>
                          Mark All as Clear
                        </button>

                        {selectedVigRowKeys.length > 0 && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0 ms-2 text-decoration-none fw-semibold"
                            onClick={() => setSelectedVigRowKeys([])}
                            style={{ fontSize: "12px" }}
                          >
                            Clear Selection ({selectedVigRowKeys.length})
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border shadow-xs mb-4">
                  <div className="card-body p-0">
                    <Table
                      columns={columnsVigilance}
                      dataSource={filteredVigList}
                      paginationConfig={pagination}
                      Selection={true}
                      selectAllAcrossPages={true}
                      onRowSelection={(keys: any[]) => setSelectedVigRowKeys(keys)}
                      loading={loadingWrCleared}
                      scroll={{ x: "max-content" }}
                      handlePagination={(pag: any) => {
                        setPagination({
                          current: pag.current,
                          pageSize: pag.pageSize
                        });
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap">
                        {/* Vigilance Status Filter Dropdown */}
                        <div className="d-flex align-items-center gap-1.5">
                          <span className="text-muted small text-nowrap fw-medium" style={{ fontSize: "12px" }}>
                            <i className="ti ti-filter me-1 text-primary"></i>Status Filter:
                          </span>
                          <select
                            className="form-select form-select-sm"
                            value={vigStatusFilter}
                            onChange={(e) => setVigStatusFilter(e.target.value)}
                            style={{
                              width: "150px",
                              height: "32px",
                              fontSize: "12px",
                              borderRadius: "6px",
                              borderColor: vigStatusFilter !== "ALL" ? "#3b82f6" : "#dee2e6",
                              backgroundColor: vigStatusFilter !== "ALL" ? "#eff6ff" : "#fff",
                              fontWeight: vigStatusFilter !== "ALL" ? 600 : 400
                            }}
                          >
                            <option value="ALL">All Statuses ({wrClearedList.length})</option>
                            {VIGILANCE_STATUS_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt} ({vigStatusCounts[opt] || 0})
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Search Input */}
                        <div className="position-relative" style={{ minWidth: "180px", maxWidth: "240px" }}>
                          <input
                            type="search"
                            className="form-control form-control-sm"
                            placeholder="Search candidates..."
                            value={vigSearchTerm}
                            onChange={(e) => {
                              setVigSearchTerm(e.target.value);
                              setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            style={{ height: "32px", fontSize: "12px", borderRadius: "6px" }}
                          />
                        </div>

                        {/* Export to Excel Button */}
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm d-inline-flex align-items-center gap-1.5 bg-white shadow-xs fw-semibold"
                          onClick={handleExportVigilanceExcel}
                          disabled={wrClearedList.length === 0}
                          style={{ height: "32px", borderRadius: "6px", fontSize: "12px" }}
                          title="Export vigilance clearance table to Excel"
                        >
                          <i className="ti ti-file-spreadsheet text-success" style={{ fontSize: "15px" }}></i>
                          <span>Export Excel</span>
                        </button>
                      </div>
                    </Table>
                  </div>
                </div>

                <div className="mt-5 d-flex justify-content-between">
                  <button className="btn btn-light border px-4" onClick={goPrev} disabled={isSettingVigilance}>Back</button>
                </div>
              </div>
            )}

            {/* STEP 7: Summary & Finalize */}
            {step === 7 && (
              <div className="animate-fade-in">
                <h5 className="mb-1 fw-bold text-dark">Step 7/7: Summary & Finalize</h5>
                <p className="text-muted small mb-4">Review initiated cycle stats before finishing.</p>

                <div className="card bg-success-light border-0 mb-4" style={{ backgroundColor: "rgba(22, 163, 74, 0.05)", borderRadius: "12px" }}>
                  <div className="card-body p-4 d-flex align-items-center">
                    <div className="bg-success rounded-circle p-2 text-white me-3 d-inline-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px" }}>
                      <i className="ti ti-check fs-3"></i>
                    </div>
                    <div>
                      <h5 className="fw-bold text-success mb-1">Cycle Ready for Review!</h5>
                      <p className="text-dark-success small mb-0">Calculations, suitability and clearances have run successfully. Committee members can now log in to review candidates.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-light p-4 rounded-3 border mb-4">
                  <h6 className="fw-bold text-dark mb-3">Probation Cycle Summary</h6>
                  <div className="row g-3">
                    <div className="col-sm-6">
                      <span className="text-muted small d-block">CYCLE ID</span>
                      <strong className="text-dark">#{cycleId}</strong>
                    </div>
                    <div className="col-sm-6">
                      <span className="text-muted small d-block">FINANCIAL YEAR</span>
                      <strong className="text-dark">{financialYear}</strong>
                    </div>
                    <div className="col-sm-6">
                      <span className="text-muted small d-block">EMPLOYEE TYPE</span>
                      <strong className="text-dark">{employeeType}</strong>
                    </div>
                    <div className="col-sm-6">
                      <span className="text-muted small d-block">PAY SCALE</span>
                      <strong className="text-dark">{grades.join(", ")}</strong>
                    </div>
                    <div className="col-sm-6">
                      <span className="text-muted small d-block">CONFIRMATION DUE ON</span>
                      <strong className="text-dark">{confirmationDueOn || "N/A"}</strong>
                    </div>
                    {employeeRange && (
                      <div className="col-sm-6">
                        <span className="text-muted small d-block">EMPLOYEE RANGE</span>
                        <strong className="text-dark">{employeeRange === "below_5000" ? "Below 5000" : "Above 5000"}</strong>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-5 d-flex justify-content-end">
                  <button className="btn btn-primary btn-lg px-5 py-2.5" onClick={handleFinish} disabled={isFinishing}>
                    {isFinishing ? "Finishing..." : "Finish & View Dashboard"}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {showProgressModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.45)",
            backdropFilter: "blur(5px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            className="card border-0 text-center p-4 p-md-5 mx-3"
            style={{
              maxWidth: "480px",
              borderRadius: "16px",
              backgroundColor: "#ffffff",
              boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)"
            }}
          >
            <div className="card-body">
              {/* Spinner */}
              <div className="d-flex justify-content-center mb-4">
                <div className="spinner-border text-danger" role="status" style={{ width: "3.5rem", height: "3.5rem", borderWidth: "4px" }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>

              {/* Title & message */}
              <h5 className="fw-bold text-dark mb-2" style={{ fontSize: "18px" }}>
                {modalText}
              </h5>
              <p className="text-secondary small mb-3 px-3" style={{ fontSize: "14px", lineHeight: "1.6" }}>
                It will take a few minutes to sync the data. Please do not close or refresh this page.
              </p>

              {/* Timeline Progress Bar */}
              <div className="px-3">
                <div className="progress" style={{ height: "8px", borderRadius: "4px", backgroundColor: "#e9ecef" }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated bg-danger"
                    role="progressbar"
                    style={{
                      width: `${progressPercent}%`,
                      transition: "width 0.8s linear",
                      borderRadius: "4px"
                    }}
                  ></div>
                </div>
                <div className="d-flex justify-content-between text-muted small mt-2" style={{ fontSize: "11px" }}>
                  <span>{progressStatus}</span>
                  <span className="fw-bold text-danger">{progressPercent}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* D&AR Clearance Details Modal */}
      {selectedDarCandidate && (() => {
        const dar = getDarDetails(selectedDarCandidate);
        const modalEmpId = selectedDarCandidate.empNo || selectedDarCandidate.emp_no || selectedDarCandidate.empId || selectedDarCandidate.key;
        const evalObj = selectedDarCandidate.dar_evaluation || selectedDarCandidate.darEvaluation || selectedDarCandidate;
        const rawStatus = (evalObj.status ?? evalObj.darStatus ?? evalObj.dar_status ?? selectedDarCandidate.status ?? selectedDarCandidate.darStatus ?? "").toString().trim();
        const statusLower = rawStatus.toLowerCase();

        const isChargeSheet = statusLower === "chargesheet issued" || statusLower === "chargesheet_issued" || statusLower === "chargesheetissued";
        const isMinor = statusLower === "minor penalty" || statusLower === "minor_penalty" || statusLower === "minorpenalty";
        const isMajor = statusLower === "major penalty" || statusLower === "major_penalty" || statusLower === "majorpenalty";
        const isClearOrNone = !isChargeSheet && !isMinor && !isMajor;

        return (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={{ backgroundColor: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", zIndex: 1060 }}
          >
            <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "520px" }}>
              <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "14px", overflow: "hidden" }}>
                {/* Header */}
                <div className="modal-header py-3 px-4 text-white" style={{ backgroundColor: "#0f172a" }}>
                  <div className="d-flex align-items-center gap-2">
                    <i className="ti ti-shield-alert text-warning fs-4"></i>
                    <h5 className="modal-title fw-bold text-white mb-0" style={{ fontSize: "16px" }}>
                      D&AR Clearance Details
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="btn-close btn-close-white shadow-none"
                    onClick={() => setSelectedDarCandidate(null)}
                  />
                </div>

                {/* Body */}
                <div className="modal-body p-4">
                  {/* Candidate Card */}
                  <div className="p-3 mb-4 rounded-3 border bg-light">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <h5 className="fw-bold text-dark mb-0" style={{ fontSize: "16px" }}>
                          {selectedDarCandidate.name || selectedDarCandidate.empName || "—"}
                        </h5>
                        <div className="text-muted small mt-1">
                          Employee ID: <span className="fw-bold text-dark">{modalEmpId || "—"}</span>
                        </div>
                      </div>
                      <span className={`badge ${dar.badgeClass} flex-shrink-0`} style={{ ...dar.badgeStyle, fontSize: "12px", padding: "6px 12px" }}>
                        {dar.statusText}
                      </span>
                    </div>
                  </div>

                  {/* Applicable D&AR Fields */}
                  <div className="d-flex flex-column gap-2.5" style={{ fontSize: "13px" }}>
                    <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                      <span className="text-secondary font-medium">Case No.</span>
                      <span className="fw-semibold text-dark">{dar.caseNo}</span>
                    </div>

                    {(isChargeSheet || isClearOrNone) && (
                      <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                        <span className="text-secondary font-medium">Charge Sheet Issued Date</span>
                        <span className="fw-semibold text-dark">{dar.chargeSheetIssuedDate}</span>
                      </div>
                    )}

                    {(isMinor || isMajor || isClearOrNone) && (
                      <div className="pb-2 border-bottom">
                        <span className="text-secondary font-medium d-block mb-1">Penalty Description</span>
                        <div className="p-2.5 rounded bg-light border text-dark fw-medium" style={{ whiteSpace: "pre-wrap", fontSize: "12px", lineHeight: "1.5" }}>
                          {dar.penaltyDescription}
                        </div>
                      </div>
                    )}

                    {(isMinor || isClearOrNone) && (
                      <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                        <span className="text-secondary font-medium">Date of Action</span>
                        <span className="fw-semibold text-dark">{dar.dateOfAction}</span>
                      </div>
                    )}

                    {(isMajor || isClearOrNone) && (
                      <>
                        <div className="d-flex justify-content-between align-items-center pb-2 border-bottom">
                          <span className="text-secondary font-medium">Penalty Start Date</span>
                          <span className="fw-semibold text-dark">{dar.penaltyStartDate}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center pb-2">
                          <span className="text-secondary font-medium">Penalty End Date</span>
                          <span className="fw-semibold text-dark">{dar.penaltyEndDate}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="modal-footer py-2.5 px-4 bg-light border-top d-flex justify-content-end" style={{ borderRadius: "0 0 14px 14px" }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm px-4 fw-semibold"
                    onClick={() => setSelectedDarCandidate(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default NewProbationCycle;
